// src/utils/pdfGenerator.js
import { PDFDocument, rgb, degrees } from 'pdf-lib';

import { saveAs } from 'file-saver';
import { calculateScheduleD } from './schedDCalculator';



// ===========================================================================
// HELPER: Smart multi-item text wrapping for description fields
// ===========================================================================

/**
 * Splits a list of items across two lines intelligently
 * @param {Array} items - Array of strings to combine
 * @param {number} maxCharsPerLine - Max characters per line
 * @returns {Object} - {line1: string, line2: string}
 */
const splitItemsAcrossLines = (items, maxCharsPerLine = 50) => {
    if (!items || items.length === 0) return { line1: '', line2: '' };
    
    // Filter out empty items and join with comma-space
    const validItems = items.filter(item => item && item.trim() !== '');
    const fullText = validItems.join(', ');
    
    if (fullText.length <= maxCharsPerLine) {
        // Fits on one line
        return { line1: fullText, line2: '' };
    }
    
    // Need to split across two lines
    // Find the last comma before maxCharsPerLine
    let splitIndex = fullText.lastIndexOf(',', maxCharsPerLine);
    
    if (splitIndex === -1) {
        // No comma found, split at last space
        splitIndex = fullText.lastIndexOf(' ', maxCharsPerLine);
    }
    
    if (splitIndex === -1 || splitIndex === 0) {
        // No good split point, hard split at max length
        return {
            line1: fullText.substring(0, maxCharsPerLine),
            line2: fullText.substring(maxCharsPerLine)
        };
    }
    
    // Split after the comma (or space)
    const line1 = fullText.substring(0, splitIndex + 1).trim();
    const line2 = fullText.substring(splitIndex + 1).trim();
    
    return { line1, line2 };
};

const drawWatermark = (page, font) => {
  const { width, height } = page.getSize();
  page.drawText('DRAFT - DO NOT FILE', {
    x: 50,
    y: height / 2,
    size: 60,
    font: font,
    color: rgb(0.95, 0.1, 0.1), // Red
    opacity: 0.3, // Semi-transparent
    rotate: degrees(45),
  });
};

// --- Helper for Dependent Qualifications ---
const RELATIONSHIP_OPTIONS = [
  'Son', 'Daughter', 'Foster Child', 'Brother', 'Sister', 'Grandchild',
  'Parent', 'Grandparent', 'Niece', 'Nephew', 'Aunt', 'Uncle', 'Other'
];
const CHILD_RELATIONSHIPS = ['Son', 'Daughter', 'Foster Child', 'Brother', 'Sister', 'Grandchild', 'Niece', 'Nephew'];

const checkQualifications = (dependent, taxYear) => {
  const qualifications = {
    isQualifyingChildForCTC: false,
    isQualifyingRelativeForODC: false,
  };
  if (!dependent || !dependent.dob || !dependent.relationship) return qualifications;
  const birthDate = new Date(dependent.dob);
  const age = taxYear - birthDate.getFullYear();

  const isChildRelationship = CHILD_RELATIONSHIPS.includes(dependent.relationship);
  if (isChildRelationship && age < 17) {
    qualifications.isQualifyingChildForCTC = true;
  }

  if (!qualifications.isQualifyingChildForCTC) {
    const meetsRelationshipTest = dependent.livedWithYou || RELATIONSHIP_OPTIONS.includes(dependent.relationship);
    const meetsGrossIncomeTest = dependent.grossIncome < 4700;
    const meetsSupportTest = dependent.providedHalfSupport;
    if (meetsRelationshipTest && meetsGrossIncomeTest && meetsSupportTest) {
      qualifications.isQualifyingRelativeForODC = true;
    }
  }
  return qualifications;
};

export const generate1040 = async (formData, totals, investmentsData = [], isPaid = false) => {
  try {
    const existingPdfBytes = await fetch('/f1040.pdf').then((res) => res.arrayBuffer());
    const pdfDoc = await PDFDocument.load(existingPdfBytes);
    const form = pdfDoc.getForm();
    const taxYear = 2025; 

    const getSpouseFullName = () => {
        const parts = [
            formData.spouseFirstName, 
            formData.spouseMiddleName, 
            formData.spouseLastName
        ];
        return parts.filter(p => p && p.trim() !== '').join(' ');
    };

    // ===========================================================================
    // PAGE 1: PERSONAL INFORMATION
    // ===========================================================================

    // Fiscal Year Fields (Top of Page)
    // Fiscal Year Fields (Top of Page)
    if (formData.isFiscalYearFiler) {
        // Combine Month and Day into a single string (e.g., "06/01")
        const startDate = `${formData.fiscalYearStartMonth}/${formData.fiscalYearStartDay}`;
        const endDate = `${formData.fiscalYearEndMonth}/${formData.fiscalYearEndDay}`;

        // Note: Based on standard form ordering, these are usually the first 3 fields
        // f1_01 = Beginning Date
        // f1_02 = Ending Date
        // f1_03 = Ending Year (Suffix)
        safeFill(form, 'topmostSubform[0].Page1[0].f1_01[0]', startDate);
        safeFill(form, 'topmostSubform[0].Page1[0].f1_02[0]', endDate);
        safeFill(form, 'topmostSubform[0].Page1[0].f1_03[0]', formData.fiscalYearEndYear);
    }


    // 1. Filed pursuant to section 301.9100-2
    if (formData.filedPursuantTo301) {
        // Note: Inspect your PDF for the exact checkbox ID. 
        // It is often near the top of the hierarchy.
        safeCheck(form, 'topmostSubform[0].Page1[0].c1_1[0]'); 
    }

    // 2. Combat Zone
    if (formData.inCombatZone) {
        // Note: Inspect your PDF for the exact checkbox ID.
        safeCheck(form, 'topmostSubform[0].Page1[0].c1_2[0]');
        safeFill(form, 'topmostSubform[0].Page1[0].f1_04[0]', formData.combatZoneDescription || '');
    }

    if (formData.isDeceased || formData.isSpouseDeceased) {
        // Check the single "Deceased" box if EITHER is deceased
        // Note: You may need to inspect your PDF for the exact ID (e.g., c1_01)
        safeCheck(form, 'topmostSubform[0].Page1[0].c1_3[0]'); 
    }

    // 3. Deceased Date (Taxpayer)
    if (formData.isDeceased && formData.dateOfDeath) {
        // Format: MM DD YYYY
        const d = new Date(formData.dateOfDeath);
        // Adjust for timezone to prevent off-by-one errors
        const userTimezoneOffset = d.getTimezoneOffset() * 60000;
        const adjustedDate = new Date(d.getTime() + userTimezoneOffset);
        
        const mm = String(adjustedDate.getMonth() + 1).padStart(2, '0');
        const dd = String(adjustedDate.getDate()).padStart(2, '0');
        const yyyy = String(adjustedDate.getFullYear());

        // Note: These fields are usually separate boxes in the header
        safeFill(form, 'topmostSubform[0].Page1[0].f1_05[0]', mm);
        safeFill(form, 'topmostSubform[0].Page1[0].f1_06[0]', dd);
        safeFill(form, 'topmostSubform[0].Page1[0].f1_07[0]', yyyy);
    }

    // 4. Deceased Date (Spouse)
    if (formData.isSpouseDeceased && formData.spouseDateOfDeath) {
        const d = new Date(formData.spouseDateOfDeath);
        const userTimezoneOffset = d.getTimezoneOffset() * 60000;
        const adjustedDate = new Date(d.getTime() + userTimezoneOffset);
        
        const mm = String(adjustedDate.getMonth() + 1).padStart(2, '0');
        const dd = String(adjustedDate.getDate()).padStart(2, '0');
        const yyyy = String(adjustedDate.getFullYear());

        safeFill(form, 'topmostSubform[0].Page1[0].f1_08[0]', mm);
        safeFill(form, 'topmostSubform[0].Page1[0].f1_09[0]', dd);
        safeFill(form, 'topmostSubform[0].Page1[0].f1_10[0]', yyyy);
    }

    safeFill(form, 'topmostSubform[0].Page1[0].f1_14[0]', formData.firstName || ''); 
    safeFill(form, 'topmostSubform[0].Page1[0].f1_15[0]', formData.lastName || '');  
    safeFill(form, 'topmostSubform[0].Page1[0].f1_16[0]', formData.ssn || '');          

    if (formData.filingStatus.includes('Married')) {
        safeFill(form, 'topmostSubform[0].Page1[0].f1_17[0]', formData.spouseFirstName || ''); 
        safeFill(form, 'topmostSubform[0].Page1[0].f1_18[0]', formData.spouseLastName || ''); 
        safeFill(form, 'topmostSubform[0].Page1[0].f1_19[0]', formData.spouseSSN || '');  
    }

    safeFill(form, 'topmostSubform[0].Page1[0].Address_ReadOrder[0].f1_20[0]', formData.address || ''); 
    safeFill(form, 'topmostSubform[0].Page1[0].Address_ReadOrder[0].f1_21[0]', formData.apt || '');     
    safeFill(form, 'topmostSubform[0].Page1[0].Address_ReadOrder[0].f1_22[0]', formData.city || '');    

    if (formData.isForeignAddress) {
        safeFill(form, 'topmostSubform[0].Page1[0].Address_ReadOrder[0].f1_13[0]', '');
        safeFill(form, 'topmostSubform[0].Page1[0].Address_ReadOrder[0].f1_14[0]', '');
        safeFill(form, 'topmostSubform[0].Page1[0].Address_ReadOrder[0].f1_25[0]', formData.foreignCountry || ''); 
        safeFill(form, 'topmostSubform[0].Page1[0].Address_ReadOrder[0].f1_26[0]', formData.foreignProvince || ''); 
        safeFill(form, 'topmostSubform[0].Page1[0].Address_ReadOrder[0].f1_27[0]', formData.foreignPostalCode || '');
        
        if (formData.mainHomeInUS) {
            safeCheck(form, 'topmostSubform[0].Page1[0].c1_5[0]');
        }
    } else {
        safeFill(form, 'topmostSubform[0].Page1[0].Address_ReadOrder[0].f1_23[0]', formData.state || ''); 
        safeFill(form, 'topmostSubform[0].Page1[0].Address_ReadOrder[0].f1_24[0]', formData.zip || '');   
    }

    // Presidential Election Campaign
    if (formData.presidentialCampaign?.you) safeCheck(form, 'topmostSubform[0].Page1[0].c1_6[0]');
    if (formData.presidentialCampaign?.spouse) safeCheck(form, 'topmostSubform[0].Page1[0].c1_7[0]');

    // ===========================================================================
    // PAGE 1: FILING STATUS
    // ===========================================================================

    const status = formData.filingStatus;
    if (status === 'Single') {
        safeCheck(form, 'topmostSubform[0].Page1[0].Checkbox_ReadOrder[0].c1_8[0]');
    } else if (status === 'Married Filing Jointly') {
        safeCheck(form, 'topmostSubform[0].Page1[0].Checkbox_ReadOrder[0].c1_8[1]');
    } else if (status === 'Married Filing Separately') {
        safeCheck(form, 'topmostSubform[0].Page1[0].Checkbox_ReadOrder[0].c1_8[2]');
        safeFill(form, 'topmostSubform[0].Page1[0].Checkbox_ReadOrder[0].f1_28[0]', getSpouseFullName());
    } else if (status === 'Head of Household') {
        safeCheck(form, 'topmostSubform[0].Page1[0].c1_8[0]');
        if (formData.hohQualifyingChildName) {
            safeFill(form, 'topmostSubform[0].Page1[0].f1_29[0]', formData.hohQualifyingChildName);
        }
    } else if (status === 'Qualifying Surviving Spouse') {
        safeCheck(form, 'topmostSubform[0].Page1[0].c1_8[1]');
        if (formData.qssQualifyingChildName) {
            safeFill(form, 'topmostSubform[0].Page1[0].f1_29[0]', formData.qssQualifyingChildName);
        }
    }

    if (formData.nonresidentAlienSpouse) {
        safeCheck(form, 'topmostSubform[0].Page1[0].c1_9[0]');
        safeFill(form, 'topmostSubform[0].Page1[0].f1_30[0]', formData.nonresidentAlienSpouseName || '');
    }

    // Digital Assets
    if (formData.receivedDigitalAssets === true) {
        safeCheck(form, 'topmostSubform[0].Page1[0].c1_10[0]'); // Yes
    } else if (formData.receivedDigitalAssets === false) {
        safeCheck(form, 'topmostSubform[0].Page1[0].c1_10[1]'); // No
    }

    // ===========================================================================
    // PAGE 1: DEPENDENTS
    // ===========================================================================

    const dependents = formData.dependents || [];

    // Checkbox for "More than 4 dependents"
    if (dependents.length > 4) {
        safeCheck(form, 'topmostSubform[0].Page1[0].c1_32[0]'); 
    }

    // Loop through up to 4 dependents
    for (let i = 0; i < Math.min(4, dependents.length); i++) {
        const dep = dependents[i];
        const rowNumText = i + 1; // Row1, Row2, Row3, Row4 for text fields
        const depNum = i + 1;     // Dependent1, Dependent2... for checkbox groups

        // --- 1. Text Fields (Rows 1-4) ---
        // Base ID starts at 31 and increments by 4 for each dependent
        // Dep 1: 31-34, Dep 2: 35-38, etc.
        const baseTextId = 31 + (i * 4);
        
        safeFill(form, `topmostSubform[0].Page1[0].Table_Dependents[0].Row${rowNumText}[0].f1_${baseTextId}[0]`, dep.firstName || '');
        safeFill(form, `topmostSubform[0].Page1[0].Table_Dependents[0].Row${rowNumText}[0].f1_${baseTextId+1}[0]`, dep.lastName || '');
        safeFill(form, `topmostSubform[0].Page1[0].Table_Dependents[0].Row${rowNumText}[0].f1_${baseTextId+2}[0]`, dep.ssn || '');
        safeFill(form, `topmostSubform[0].Page1[0].Table_Dependents[0].Row${rowNumText}[0].f1_${baseTextId+3}[0]`, dep.relationship || '');

        // --- 2. Lived with You / US (Row 5) ---
        // Base ID starts at 12 and increments by 2
        const baseRow5Id = 12 + (i * 2);
        
        if (dep.livedWithYou) {
            safeCheck(form, `topmostSubform[0].Page1[0].Table_Dependents[0].Row5[0].Dependent${depNum}[0].c1_${baseRow5Id}[0]`);
        }
        if (dep.livedInUS) {
            safeCheck(form, `topmostSubform[0].Page1[0].Table_Dependents[0].Row5[0].Dependent${depNum}[0].c1_${baseRow5Id+1}[0]`);
        }

        // --- 3. Student / Disabled (Row 6) ---
        // Base ID starts at 20 and increments by 2
        const baseRow6Id = 20 + (i * 2);
        
        if (dep.fullTimeStudent) {
            safeCheck(form, `topmostSubform[0].Page1[0].Table_Dependents[0].Row6[0].Dependent${depNum}[0].c1_${baseRow6Id}[0]`);
        }
        if (dep.permanentlyDisabled) {
            safeCheck(form, `topmostSubform[0].Page1[0].Table_Dependents[0].Row6[0].Dependent${depNum}[0].c1_${baseRow6Id+1}[0]`);
        }

        // --- 4. Credits: CTC / ODC (Row 7) ---
        // Base ID starts at 28 and increments by 1
        // CTC is index [0], ODC is index [1]
        const row7Id = 28 + i;
        const q = checkQualifications(dep, taxYear);
        
        if (q.isQualifyingChildForCTC) {
            safeCheck(form, `topmostSubform[0].Page1[0].Table_Dependents[0].Row7[0].Dependent${depNum}[0].c1_${row7Id}[0]`);
        }
        if (q.isQualifyingRelativeForODC) {
            safeCheck(form, `topmostSubform[0].Page1[0].Table_Dependents[0].Row7[0].Dependent${depNum}[0].c1_${row7Id}[1]`);
        }
    }


    // ===========================================================================
    // PAGE 1: INCOME (Lines 1a - 1z)
    // ===========================================================================

    safeFill(form, 'topmostSubform[0].Page1[0].f1_47[0]', formatMoney(totals.totalW2Wages)); // 1a
    safeFill(form, 'topmostSubform[0].Page1[0].f1_48[0]', formatMoney(totals.totalHouseholdWages)); // 1b
    safeFill(form, 'topmostSubform[0].Page1[0].f1_49[0]', formatMoney(totals.totalTipIncome)); // 1c
    safeFill(form, 'topmostSubform[0].Page1[0].f1_50[0]', formatMoney(totals.totalWaiverPayments)); // 1d
    safeFill(form, 'topmostSubform[0].Page1[0].f1_51[0]', formatMoney(formData.dependentCare?.form2441_line26)); // 1e
    safeFill(form, 'topmostSubform[0].Page1[0].f1_52[0]', formatMoney(formData.adoptionCredit?.form8839_line31)); // 1f
    safeFill(form, 'topmostSubform[0].Page1[0].f1_53[0]', formatMoney(totals.form_8919_total_wages)); // 1g

    if (totals.otherEarnedIncome > 0 && formData.otherEarnedIncomeType) {
        safeFill(form, 'topmostSubform[0].Page1[0].f1_54[0]', formData.otherEarnedIncomeType);
        safeFill(form, 'topmostSubform[0].Page1[0].f1_55[0]', formatMoney(totals.otherEarnedIncome));
    }

    safeFill(form, 'topmostSubform[0].Page1[0].f1_56[0]', formatMoney(formData.nonTaxableCombatPayAmount)); // 1i
    safeFill(form, 'topmostSubform[0].Page1[0].f1_57[0]', formatMoney(totals.line1z)); // 1z

    // ===========================================================================
    // PAGE 1: INCOME (Lines 2 - 11a)
    // ===========================================================================

    safeFill(form, 'topmostSubform[0].Page1[0].f1_58[0]', formatMoney(formData.taxExemptInterest)); // 2a
    safeFill(form, 'topmostSubform[0].Page1[0].f1_59[0]', formatMoney(totals.line2b)); // 2b

    safeFill(form, 'topmostSubform[0].Page1[0].f1_60[0]', formatMoney(formData.qualifiedDividends)); // 3a
    safeFill(form, 'topmostSubform[0].Page1[0].f1_61[0]', formatMoney(totals.line3b)); // 3b

    // 3c - Child's dividends included checkboxes (Form 8814)
    if (formData.childsQualifiedDividendsIncluded) {
        safeCheck(form, 'topmostSubform[0].Page1[0].c1_33[0]'); // Box 1
    }
    if (formData.childsOrdinaryDividendsIncluded) {
        safeCheck(form, 'topmostSubform[0].Page1[0].c1_34[0]'); // Box 2
    }


    // Line 4: IRA Distributions
    safeFill(form, `topmostSubform[0].Page1[0].f1_62[0]`, formatMoney(formData.totalIraDistributions)); // 4a
    safeFill(form, `topmostSubform[0].Page1[0].f1_63[0]`, formatMoney(totals.line4b)); // 4b

    // 4c - IRA Checkboxes
    if (formData.iraRollover) {
        safeCheck(form, `topmostSubform[0].Page1[0].c1_35[0]`); // Box 1
    }
    if (formData.iraQCD) {
        safeCheck(form, `topmostSubform[0].Page1[0].c1_36[0]`); // Box 2
    }
    if (formData.iraHFD) {
        safeCheck(form, `topmostSubform[0].Page1[0].c1_37[0]`); // Box 3
        // Fill the text code (e.g., "HFD") next to Box 3
        // Note: Verify this field ID in your PDF template
        safeFill(form, `topmostSubform[0].Page1[0].f1_64[0]`, formData.iraBox3Code || 'HFD'); 
    }

    // Line 5: Pensions and Annuities
    safeFill(form, `topmostSubform[0].Page1[0].f1_65[0]`, formatMoney(formData.totalPensionsAnnuities)); // 5a
    safeFill(form, `topmostSubform[0].Page1[0].f1_66[0]`, formatMoney(totals.line5b)); // 5b

    // 5c - Pension Checkboxes
    if (formData.pensionRollover) {
        safeCheck(form, `topmostSubform[0].Page1[0].c1_38[0]`); // Box 1
    }
    if (formData.pensionPSO) {
        safeCheck(form, `topmostSubform[0].Page1[0].c1_39[0]`); // Box 2
    }
    if (formData.pensionBox3) {
        safeCheck(form, `topmostSubform[0].Page1[0].c1_40[0]`); // Box 3
        // Fill the text code next to Box 3
        // Note: Verify this field ID in your PDF template
        safeFill(form, `topmostSubform[0].Page1[0].f1_67[0]`, formData.pensionBox3Code || ''); 
    }

    // Line 6: Social Security
    safeFill(form, `topmostSubform[0].Page1[0].f1_68[0]`, formatMoney(formData.totalSocialSecurityBenefits)); // 6a
    safeFill(form, `topmostSubform[0].Page1[0].f1_69[0]`, formatMoney(totals.line6b)); // 6b

    // 6c - Lump-sum election
    if (formData.lumpSumElection) {
        safeCheck(form, `topmostSubform[0].Page1[0].c1_41[0]`); 
    }

    // 6d - MFS lived apart checkbox
    if (status === 'Married Filing Separately' && formData.livedApartEntireYear) {
        safeCheck(form, `topmostSubform[0].Page1[0].c1_42[0]`);
    }

    // Line 7: Capital Gains
    safeFill(form, `topmostSubform[0].Page1[0].f1_70[0]`, formatMoney(totals.line7)); // 7a

    // 7b - Schedule D checkboxes
    if (formData.scheduleDNotRequired) {
        safeCheck(form, `topmostSubform[0].Page1[0].c1_43[0]`); // Box 1: Sched D not required
    }
    
    if (formData.includesChildsCapitalGain) {
        safeCheck(form, `topmostSubform[0].Page1[0].c1_44[0]`); // Box 2: Includes child's gain
        
        // --- NEW: Fill the amount on the dotted line for 7b ---
        // Note: The Field ID for this specific dotted line varies by PDF vendor.
        // Common IDs are often related to the checkbox or the line number.
        // If 'f1_74_child' doesn't work, you may need to inspect your PDF to find the exact ID (e.g., f1_74a, f1_74_text, etc.)
        safeFill(form, `topmostSubform[0].Page1[0].f1_71[0]`, formatMoney(formData.childCapitalGainAmount)); 
    }

    safeFill(form, `topmostSubform[0].Page1[0].f1_72[0]`, formatMoney(totals.line8)); // 8 (Schedule 1)
    safeFill(form, `topmostSubform[0].Page1[0].f1_73[0]`, formatMoney(totals.totalIncome_line9)); // 9 (Total Income)
    safeFill(form, `topmostSubform[0].Page1[0].f1_74[0]`, formatMoney(totals.line10)); // 10 (Adjustments)
    safeFill(form, `topmostSubform[0].Page1[0].f1_75[0]`, formatMoney(totals.agi)); // 11a (AGI)

    // ===========================================================================
    // PAGE 2: TAX AND CREDITS (Lines 11b - 24)
    // ===========================================================================

    safeFill(form, 'topmostSubform[0].Page2[0].f2_01[0]', formatMoney(totals.agi)); // 11b

    // Standard Deduction Checkboxes
    if (formData.canBeClaimedAsDependent) safeCheck(form, 'topmostSubform[0].Page2[0].c2_01[0]');
    if (formData.spouseCanBeClaimedAsDependent) safeCheck(form, 'topmostSubform[0].Page2[0].c2_02[0]');
    if (formData.spouseItemizes) safeCheck(form, 'topmostSubform[0].Page2[0].c2_03[0]');
    if (formData.isDualStatusAlien) { 
        safeCheck(form, 'topmostSubform[0].Page2[0].c2_04[0]');
    }
    if (formData.isOver65) safeCheck(form, 'topmostSubform[0].Page2[0].c2_05[0]');
    if (formData.isBlind) safeCheck(form, 'topmostSubform[0].Page2[0].c2_06[0]');
    if (formData.spouseIsOver65) safeCheck(form, 'topmostSubform[0].Page2[0].c2_07[0]');
    if (formData.spouseIsBlind) safeCheck(form, 'topmostSubform[0].Page2[0].c2_08[0]');

    safeFill(form, 'topmostSubform[0].Page2[0].f2_02[0]', formatMoney(totals.line12)); // 12 (Deduction)
    safeFill(form, 'topmostSubform[0].Page2[0].f2_03[0]', formatMoney(totals.line13a)); // 13a (QBI)
    safeFill(form, 'topmostSubform[0].Page2[0].f2_04[0]', formatMoney(totals.line13b)); // 13b (Sched 1-A)
    safeFill(form, 'topmostSubform[0].Page2[0].f2_05[0]', formatMoney(totals.line14)); // 14 (Total Deds)
    safeFill(form, 'topmostSubform[0].Page2[0].f2_06[0]', formatMoney(totals.taxableIncome)); // 15 (Taxable Inc)

    const val_line16 = parseFloat(totals.line16) || 0;

    // Checkbox 1: Form 8814
    if (formData.form8814) {
        safeCheck(form, 'topmostSubform[0].Page2[0].c2_9[0]');
    }

    // Checkbox 2: Form 4972
    if (formData.form4972) {
        safeCheck(form, 'topmostSubform[0].Page2[0].c2_10[0]');
    }

    // Checkbox 3: Other (Complex Logic)
    const box3Codes = [];
    
    if ((parseFloat(formData.taxSection962) || 0) > 0) box3Codes.push("962");
    if ((parseFloat(formData.taxRecaptureEducation) || 0) > 0) box3Codes.push("ECR");
    if ((parseFloat(formData.taxForm8621) || 0) > 0) box3Codes.push("1291TAX");
    if ((parseFloat(formData.taxForm8978) || 0) > 0) box3Codes.push("Form 8978");
    if ((parseFloat(formData.taxSection965i) || 0) > 0) box3Codes.push("965INC");
    if ((parseFloat(formData.otherTaxFormAmount) || 0) > 0 && formData.otherTaxFormNumber) {
        box3Codes.push(formData.otherTaxFormNumber);
    }

    if (box3Codes.length > 0) {
        safeCheck(form, 'topmostSubform[0].Page2[0].c2_``[0]'); // Check Box 3
        
        // Join codes with a comma if multiple apply
        const codeString = box3Codes.join(", ");
        safeFill(form, 'topmostSubform[0].Page2[0].f2_07[0]', codeString);
    }

    // Fill the Total Tax Amount on Line 16
    safeFill(form, 'topmostSubform[0].Page2[0].f2_08[0]', formatMoney(val_line16)); 

    safeFill(form, 'topmostSubform[0].Page2[0].f2_09[0]', formatMoney(totals.line17)); // 17 (Sched 2)
    safeFill(form, 'topmostSubform[0].Page2[0].f2_10[0]', formatMoney(totals.line18)); // 18 (Total Tax)
    safeFill(form, 'topmostSubform[0].Page2[0].f2_11[0]', formatMoney(totals.line19)); // 19 (CTC/ODC)
    safeFill(form, 'topmostSubform[0].Page2[0].f2_12[0]', formatMoney(totals.line20)); // 20 (Sched 3)
    safeFill(form, 'topmostSubform[0].Page2[0].f2_13[0]', formatMoney(totals.line21)); // 21 (Total Credits)
    safeFill(form, 'topmostSubform[0].Page2[0].f2_14[0]', formatMoney(totals.line22)); // 22 (Subtract)
    safeFill(form, 'topmostSubform[0].Page2[0].f2_15[0]', formatMoney(totals.line23)); // 23 (Other Taxes)
    safeFill(form, 'topmostSubform[0].Page2[0].f2_16[0]', formatMoney(totals.line24)); // 24 (Total Tax)

    // ===========================================================================
    // PAGE 2: PAYMENTS (Lines 25 - 33)
    // ===========================================================================

    safeFill(form, 'topmostSubform[0].Page2[0].f2_17[0]', formatMoney(totals.line25a)); // 25a
    safeFill(form, 'topmostSubform[0].Page2[0].f2_18[0]', formatMoney(totals.line25b)); // 25b
    safeFill(form, 'topmostSubform[0].Page2[0].f2_19[0]', formatMoney(totals.line25c)); // 25c
    safeFill(form, 'topmostSubform[0].Page2[0].f2_20[0]', formatMoney(totals.line25d)); // 25d

    safeFill(form, 'topmostSubform[0].Page2[0].f2_21[0]', formatMoney(totals.line26)); // 26 (Est Tax)
    // --- NEW: Line 26 SSN (Divorced/Separated) ---
    if (formData.formerSpouseSSN) {
        // Note: f2_22 is the standard ID for the text field next to Line 26
        safeFill(form, 'topmostSubform[0].Page2[0].SSN_ReadOrder[0].f2_22[0]', formData.formerSpouseSSN);
    }
    // Line 27 - Earned Income Credit
    safeFill(form, 'topmostSubform[0].Page2[0].f2_23[0]', formatMoney(totals.line27)); 

    // 27b - Clergy filing Schedule SE
    if (formData.clergyFilingSE) {
        safeCheck(form, 'topmostSubform[0].Page2[0].c2_12[0]');
    }

    // 27c - Do not claim EIC
    if (formData.doNotClaimEIC) {
        safeCheck(form, 'topmostSubform[0].Page2[0].c2_13[0]');
    }

    // Line 28 - Additional Child Tax Credit
    safeFill(form, 'topmostSubform[0].Page2[0].f2_24[0]', formatMoney(totals.line28));

    // Line 28 Checkbox - Form 8862
    if (formData.line28Form8862) {
        safeCheck(form, 'topmostSubform[0].Page2[0].Line28_ReadOrder[0].c2_14[0]');
    }
    safeFill(form, 'topmostSubform[0].Page2[0].f2_25[0]', formatMoney(totals.line29)); // 29 (AOC)
    safeFill(form, 'topmostSubform[0].Page2[0].f2_26[0]', formatMoney(totals.line30)); // 30 (Reserved/Adoption)
    safeFill(form, 'topmostSubform[0].Page2[0].f2_27[0]', formatMoney(totals.line31)); // 31 (Net PTC)
    safeFill(form, 'topmostSubform[0].Page2[0].f2_28[0]', formatMoney(totals.line32)); // 32 (Total Refundable)
    safeFill(form, 'topmostSubform[0].Page2[0].f2_29[0]', formatMoney(totals.line33)); // 33 (Total Payments)

    // ===========================================================================
    // PAGE 2: REFUND OR AMOUNT OWED (Lines 34 - 38)
    // ===========================================================================

    // Use the calculated values from App.js (totals object)
    safeFill(form, 'topmostSubform[0].Page2[0].f2_30[0]', formatMoney(totals.line34)); // 34 (Overpaid)
    safeFill(form, 'topmostSubform[0].Page2[0].f2_31[0]', formatMoney(totals.line35a)); // 35a (Refund)

    // Direct Deposit
    safeFill(form, 'topmostSubform[0].Page2[0].RoutingNo[0].f2_32[0]', formData.refundRoutingNumber || ''); // 35b
    if (formData.refundAccountType === 'Checking') safeCheck(form, 'topmostSubform[0].Page2[0].c2_16[0]');
    if (formData.refundAccountType === 'Savings') safeCheck(form, 'topmostSubform[0].Page2[0].c2_16[1]');
    safeFill(form, 'topmostSubform[0].Page2[0].AccountNo[0].f2_33[0]', formData.refundAccountNumber || ''); // 35d

    safeFill(form, 'topmostSubform[0].Page2[0].f2_34[0]', formatMoney(totals.line36)); // 36 (Applied)
    safeFill(form, 'topmostSubform[0].Page2[0].f2_35[0]', formatMoney(totals.line37)); // 37 (Owe)
    safeFill(form, 'topmostSubform[0].Page2[0].f2_36[0]', formatMoney(totals.line38)); // 38 (Penalty)

    // ===========================================================================
    // PAGE 2: THIRD PARTY DESIGNEE & SIGNATURES
    // ===========================================================================

    if (formData.thirdPartyDesignee?.wantsToDesignate) {
        safeCheck(form, 'topmostSubform[0].Page2[0].c2_17[0]'); // Yes
        safeFill(form, 'topmostSubform[0].Page2[0].f2_37[0]', formData.thirdPartyDesignee.name || '');
        safeFill(form, 'topmostSubform[0].Page2[0].f2_38[0]', formData.thirdPartyDesignee.phone || '');
        safeFill(form, 'topmostSubform[0].Page2[0].f2_39[0]', formData.thirdPartyDesignee.pin || '');
    } else {
        safeCheck(form, 'topmostSubform[0].Page2[0].c2_17[1]'); // No
    }

    safeFill(form, 'topmostSubform[0].Page2[0].f2_40[0]', formData.signatures?.primaryOccupation || '');
    safeFill(form, 'topmostSubform[0].Page2[0].f2_41[0]', formData.signatures?.primaryIpPin || '');

    if (formData.filingStatus.includes('Married')) {
        safeFill(form, 'topmostSubform[0].Page2[0].f2_42[0]', formData.signatures?.spouseOccupation || '');
        safeFill(form, 'topmostSubform[0].Page2[0].f2_43[0]', formData.signatures?.spouseIpPin || '');
    }

    safeFill(form, 'topmostSubform[0].Page2[0].f2_44[0]', formData.signatures?.phoneNumber || '');
    safeFill(form, 'topmostSubform[0].Page2[0].f2_45[0]', formData.signatures?.emailAddress || '');

    // Paid Preparer
    if (formData.paidPreparer?.usedPaidPreparer) {
        safeFill(form, 'topmostSubform[0].Page2[0].f2_46[0]', formData.paidPreparer.preparerName || '');
        safeFill(form, 'topmostSubform[0].Page2[0].f2_47[0]', formData.paidPreparer.ptin || '');
        if (formData.paidPreparer.selfEmployed) safeCheck(form, 'topmostSubform[0].Page2[0].c2_18[0]');
        safeFill(form, 'topmostSubform[0].Page2[0].f2_48[0]', formData.paidPreparer.firmName || '');
        safeFill(form, 'topmostSubform[0].Page2[0].f2_49[0]', formData.paidPreparer.phone || '');
        safeFill(form, 'topmostSubform[0].Page2[0].f2_50[0]', formData.paidPreparer.address || '');
        safeFill(form, 'topmostSubform[0].Page2[0].f2_51[0]', formData.paidPreparer.firmEin || '');
    }
    
    // ===========================================================================
    // SCHEDULE 1: ADDITIONAL INCOME & ADJUSTMENTS (Separate PDF)
    // ===========================================================================

    const isSchedule1Required = (totals.sched1_line10 !== 0) || (totals.sched1_line26 !== 0);

    if (isSchedule1Required) {
        try {
            const s1Bytes = await fetch('/f1040s1.pdf').then(res => res.arrayBuffer());
            const s1Doc = await PDFDocument.load(s1Bytes);
            const s1Form = s1Doc.getForm();
            const fullName = `${formData.firstName || ''} ${formData.lastName || ''}`.trim();
            
            // --- HEADER ---
            safeFill(s1Form, 'topmostSubform[0].Page1[0].f1_01[0]', fullName); 
            safeFill(s1Form, 'topmostSubform[0].Page1[0].f1_02[0]', formData.ssn || '');

            // --- PART I: ADDITIONAL INCOME ---
            
            // 1099-K Personal Item Loss (New field at top)
            safeFill(s1Form, 'topmostSubform[0].Page1[0].f1_03[0]', formatMoney(totals.saleOfPersonalItems1099K || 0));

            // Line 1: Taxable Refunds
            safeFill(s1Form, 'topmostSubform[0].Page1[0].f1_04[0]', formatMoney(totals.sched1_line1));
            
            // Line 2a: Alimony Received
            safeFill(s1Form, 'topmostSubform[0].Page1[0].f1_05[0]', formatMoney(totals.sched1_line2a));
            
            // Line 2b: Date of Divorce
            if (totals.sched1_line2a > 0 && formData.divorceDate) {
                const d = new Date(formData.divorceDate);
                // Adjust for timezone
                const userTimezoneOffset = d.getTimezoneOffset() * 60000;
                const adjustedDate = new Date(d.getTime() + userTimezoneOffset);
                const dateStr = `${adjustedDate.getMonth() + 1}/${adjustedDate.getDate()}/${adjustedDate.getFullYear()}`;
                safeFill(s1Form, 'topmostSubform[0].Page1[0].f1_06[0]', dateStr);
            }

            // Line 3: Business Income
            safeFill(s1Form, 'topmostSubform[0].Page1[0].f1_07[0]', formatMoney(totals.sched1_line3));
            
            // Line 4: Other Gains (Form 4797)
            safeFill(s1Form, 'topmostSubform[0].Page1[0].f1_08[0]', formatMoney(totals.sched1_line4));
            
            // Line 5: Rental/Royalty/Partnership (Schedule E)
            safeFill(s1Form, 'topmostSubform[0].Page1[0].f1_09[0]', formatMoney(totals.sched1_line5));
            
            // Line 6: Farm Income (Schedule F)
            safeFill(s1Form, 'topmostSubform[0].Page1[0].f1_10[0]', formatMoney(totals.sched1_line6));
            
            // Line 7: Unemployment Compensation
            safeFill(s1Form, 'topmostSubform[0].Page1[0].f1_12[0]', formatMoney(totals.sched1_line7));

            // --- Line 8 Breakdown ---
            safeFill(s1Form, 'topmostSubform[0].Page1[0].f1_13[0]', formatMoney(formData.netOperatingLoss || 0)); // 8a
            safeFill(s1Form, 'topmostSubform[0].Page1[0].f1_14[0]', formatMoney(formData.gamblingWinnings || 0)); // 8b
            safeFill(s1Form, 'topmostSubform[0].Page1[0].f1_15[0]', formatMoney(formData.cancellationOfDebt || 0)); // 8c
            safeFill(s1Form, 'topmostSubform[0].Page1[0].f1_16[0]', formatMoney(formData.foreignEarnedIncomeExclusion || 0)); // 8d
            safeFill(s1Form, 'topmostSubform[0].Page1[0].f1_17[0]', formatMoney(formData.incomeFrom8853 || 0)); // 8e
            safeFill(s1Form, 'topmostSubform[0].Page1[0].f1_18[0]', formatMoney(formData.incomeFrom8889 || 0)); // 8f
            safeFill(s1Form, 'topmostSubform[0].Page1[0].f1_19[0]', formatMoney(formData.alaskaDividends || 0)); // 8g
            safeFill(s1Form, 'topmostSubform[0].Page1[0].f1_20[0]', formatMoney(formData.juryDutyPay || 0)); // 8h
            safeFill(s1Form, 'topmostSubform[0].Page1[0].f1_21[0]', formatMoney(formData.prizesAndAwards || 0)); // 8i
            safeFill(s1Form, 'topmostSubform[0].Page1[0].f1_22[0]', formatMoney(formData.hobbyIncome || 0)); // 8j
            safeFill(s1Form, 'topmostSubform[0].Page1[0].f1_23[0]', formatMoney(formData.stockOptions || 0)); // 8k
            safeFill(s1Form, 'topmostSubform[0].Page1[0].f1_24[0]', formatMoney(formData.personalPropertyRental || 0)); // 8l
            safeFill(s1Form, 'topmostSubform[0].Page1[0].f1_25[0]', formatMoney(formData.olympicPrizeMoney || 0)); // 8m
            safeFill(s1Form, 'topmostSubform[0].Page1[0].f1_26[0]', formatMoney(formData.section951a || 0)); // 8n
            safeFill(s1Form, 'topmostSubform[0].Page1[0].f1_27[0]', formatMoney(formData.section951Aa || 0)); // 8o
            safeFill(s1Form, 'topmostSubform[0].Page1[0].f1_28[0]', formatMoney(formData.excessBusinessLoss || 0)); // 8p
            safeFill(s1Form, 'topmostSubform[0].Page1[0].f1_29[0]', formatMoney(formData.ableDistributions || 0)); // 8q
            safeFill(s1Form, 'topmostSubform[0].Page1[0].f1_30[0]', formatMoney(formData.scholarships || 0)); // 8r
            safeFill(s1Form, 'topmostSubform[0].Page1[0].f1_31[0]', formatMoney(totals.sched1_line8s || 0)); // 8s (Medicaid Waiver)
            safeFill(s1Form, 'topmostSubform[0].Page1[0].f1_32[0]', formatMoney(formData.nonqualifiedPension || 0)); // 8t
            safeFill(s1Form, 'topmostSubform[0].Page1[0].f1_33[0]', formatMoney(formData.incarceratedWages || 0)); // 8u
            safeFill(s1Form, 'topmostSubform[0].Page1[0].f1_34[0]', formatMoney(formData.digitalAssetOrdinaryIncome || 0)); // 8v
            
            // Line 8z: Other Income
            // Construct description string
            const line8zItems = [];
            if (formData.strikeBenefits > 0) line8zItems.push('Strike Benefits');
            if (formData.rtaaPayments > 0) line8zItems.push('RTAA Payments');
            if (formData.correctiveDistributions > 0) line8zItems.push('Corrective Distributions');
            if (formData.disasterReliefPayments > 0) line8zItems.push('Disaster Relief');
            if (formData.coverdellTaxableDistributions > 0) line8zItems.push('Coverdell ESA');
            if (formData.line8z_other && formData.line8z_other_description) line8zItems.push(formData.line8z_other_description);
            
            const desc8z = line8zItems.join(', ');
            
            safeFill(s1Form, 'topmostSubform[0].Page1[0].Line8z_ReadOrder[0].f1_35[0]', desc8z); // Description
            safeFill(s1Form, 'topmostSubform[0].Page1[0].f1_36[0]', formatMoney(totals.sched1_line8z)); // Amount

            // Line 9: Total Other Income
            safeFill(s1Form, 'topmostSubform[0].Page1[0].f1_37[0]', formatMoney(totals.sched1_line9));

            // Line 10: Total Additional Income
            safeFill(s1Form, 'topmostSubform[0].Page1[0].f1_38[0]', formatMoney(totals.sched1_line10));


            // --- PART II: ADJUSTMENTS TO INCOME ---

            safeFill(s1Form, 'topmostSubform[0].Page2[0].f2_01[0]', formatMoney(totals.sched1_line11)); // 11 Educator
            safeFill(s1Form, 'topmostSubform[0].Page2[0].f2_02[0]', formatMoney(totals.sched1_line12)); // 12 Reservist
            safeFill(s1Form, 'topmostSubform[0].Page2[0].f2_03[0]', formatMoney(totals.sched1_line13)); // 13 HSA
            safeFill(s1Form, 'topmostSubform[0].Page2[0].f2_04[0]', formatMoney(totals.sched1_line14)); // 14 Moving
            safeFill(s1Form, 'topmostSubform[0].Page2[0].f2_05[0]', formatMoney(totals.sched1_line15)); // 15 SE Tax
            safeFill(s1Form, 'topmostSubform[0].Page2[0].f2_06[0]', formatMoney(totals.sched1_line16)); // 16 SE Retirement
            safeFill(s1Form, 'topmostSubform[0].Page2[0].f2_07[0]', formatMoney(totals.sched1_line17)); // 17 SE Health
            safeFill(s1Form, 'topmostSubform[0].Page2[0].f2_08[0]', formatMoney(totals.sched1_line18)); // 18 Penalty
            
            // Line 19: Alimony Paid
            safeFill(s1Form, 'topmostSubform[0].Page2[0].f2_09[0]', formatMoney(totals.sched1_line19a)); // 19a Amount
            if (totals.sched1_line19a > 0) {
                safeFill(s1Form, 'topmostSubform[0].Page2[0].Line19b_CombField[0].f2_10[0]', formData.alimonyRecipientSSN || ''); // 19b SSN
                
                if (formData.alimonyPaidDivorceDate) {
                    const d = new Date(formData.alimonyPaidDivorceDate);
                    const userTimezoneOffset = d.getTimezoneOffset() * 60000;
                    const adjustedDate = new Date(d.getTime() + userTimezoneOffset);
                    const dateStr = `${adjustedDate.getMonth() + 1}/${adjustedDate.getDate()}/${adjustedDate.getFullYear()}`;
                    safeFill(s1Form, 'topmostSubform[0].Page2[0].f2_11[0]', dateStr); // 19c Date
                }
            }

            safeFill(s1Form, 'topmostSubform[0].Page2[0].f2_12[0]', formatMoney(totals.sched1_line20)); // 20 IRA
            safeFill(s1Form, 'topmostSubform[0].Page2[0].f2_13[0]', formatMoney(totals.sched1_line21)); // 21 Student Loan
            safeFill(s1Form, 'topmostSubform[0].Page2[0].f2_15[0]', formatMoney(totals.sched1_line23)); // 23 Archer MSA
            
            // Line 24 Breakdown
            safeFill(s1Form, 'topmostSubform[0].Page2[0].Line24a_ReadOrder[0].f2_16[0]', formatMoney(totals.sched1_line24a)); // 24a Jury
            safeFill(s1Form, 'topmostSubform[0].Page2[0].f2_17[0]', formatMoney(totals.sched1_line24b)); // 24b
            safeFill(s1Form, 'topmostSubform[0].Page2[0].f2_18[0]', formatMoney(totals.sched1_line24c)); // 24c
            safeFill(s1Form, 'topmostSubform[0].Page2[0].f2_19[0]', formatMoney(totals.sched1_line24d)); // 24d
            safeFill(s1Form, 'topmostSubform[0].Page2[0].f2_20[0]', formatMoney(totals.sched1_line24e)); // 24e
            safeFill(s1Form, 'topmostSubform[0].Page2[0].f2_21[0]', formatMoney(totals.sched1_line24f)); // 24f
            safeFill(s1Form, 'topmostSubform[0].Page2[0].f2_22[0]', formatMoney(totals.sched1_line24g)); // 24g
            safeFill(s1Form, 'topmostSubform[0].Page2[0].f2_23[0]', formatMoney(totals.sched1_line24h)); // 24h
            safeFill(s1Form, 'topmostSubform[0].Page2[0].f2_24[0]', formatMoney(totals.sched1_line24i)); // 24i
            safeFill(s1Form, 'topmostSubform[0].Page2[0].f2_25[0]', formatMoney(totals.sched1_line24j)); // 24j
            safeFill(s1Form, 'topmostSubform[0].Page2[0].f2_26[0]', formatMoney(totals.sched1_line24k)); // 24k

            // Line 24z: Other Adjustments
            safeFill(s1Form, 'topmostSubform[0].Page2[0].Line24z_ReadOrder[0].f2_27[0]', formData.otherAdjustments24zDescription || ''); // Desc
            safeFill(s1Form, 'topmostSubform[0].Page2[0].f2_28[0]', formatMoney(totals.sched1_line24z)); // Amount

            // Line 25: Total Other Adjustments
            safeFill(s1Form, 'topmostSubform[0].Page2[0].f2_29[0]', formatMoney(totals.totalAdjustmentsSched1));

            // Line 26: Total Adjustments
            safeFill(s1Form, 'topmostSubform[0].Page2[0].f2_30[0]', formatMoney(totals.sched1_line26));

            s1Form.flatten();
            const s1Pages = await pdfDoc.copyPages(s1Doc, s1Doc.getPageIndices());
            s1Pages.forEach(page => pdfDoc.addPage(page));
        } catch (e) {
            console.error("Error loading Schedule 1:", e);
        }
    }

// ===========================================================================
// SCHEDULE 1-A: ADDITIONAL DEDUCTIONS (NEW 2025)
// ===========================================================================

// Check if Schedule 1-A is required (if total deductions > 0)
const isSchedule1ARequired = (totals.line13b > 0);

if (isSchedule1ARequired) {
    try {
        console.log("Generating Schedule 1-A...");
        
        // 1. Load the PDF from the public folder
        const s1aBytes = await fetch('/f1040s1a.pdf').then(res => res.arrayBuffer());
        const s1aDoc = await PDFDocument.load(s1aBytes);
        const s1aForm = s1aDoc.getForm();

        const firstName = formData.firstName || '';
        const lastName = formData.lastName || '';
        const fullName = `${firstName} ${lastName}`.trim();
        
        safeFill(s1aForm, 'form1[0].Page1[0].f1_01[0]', fullName); 
        safeFill(s1aForm, 'form1[0].Page1[0].f1_02[0]', formData.ssn || '');

        // --- PART I: MAGI ---
        safeFill(s1aForm, 'form1[0].Page1[0].f1_03[0]', formatMoney(totals.agi || 0)); // Line 1
        
        const puertoRico = parseFloat(formData.puertoRicoIncome) || 0;
        if (puertoRico > 0) {
            safeFill(s1aForm, 'form1[0].Page1[0].f1_04[0]', formatMoney(puertoRico));
        }

        // Line 2b: Foreign Earned Income Exclusion (Form 2555, Line 45)
        const foreignIncome = parseFloat(formData.foreignEarnedIncomeExclusion) || 0;
        if (foreignIncome > 0) {
            safeFill(s1aForm, 'form1[0].Page1[0].f1_05[0]', formatMoney(foreignIncome));
        }

        const housingExclusion = parseFloat(formData.foreignHousingExclusion) || 0;
        if (housingExclusion > 0) {
            safeFill(s1aForm, 'form1[0].Page1[0].f1_06[0]', formatMoney(housingExclusion));
        }

        // Line 2d: Possession Exclusion (Form 4563)
        const possession = parseFloat(formData.possessionExclusion) || 0;
        if (possession > 0) {
            safeFill(s1aForm, 'form1[0].Page1[0].f1_07[0]', formatMoney(possession));
        }

        // Line 2e: Total Add-backs
        const totalAddbacks = puertoRico + foreignIncome + housingExclusion + possession;
        safeFill(s1aForm, 'form1[0].Page1[0].f1_08[0]', formatMoney(totalAddbacks)); 
        
        // Line 3 (MAGI)
        safeFill(s1aForm, 'form1[0].Page1[0].f1_09[0]', formatMoney(totals.magi_s1a || 0)); 

        // --- PART II: TIPS ---
        // Line 4a: W-2 Tips
        safeFill(s1aForm, 'form1[0].Page1[0].f1_10[0]', formatMoney(parseFloat(formData.tipsW2) || 0));

        // Line 4b: Form 4137 Tips
        safeFill(s1aForm, 'form1[0].Page1[0].f1_11[0]', formatMoney(parseFloat(formData.tips4137) || 0));

        const tCalc = totals.sched1A.tipsCalc || {};
        
        // Line 4c, 5, 6, 7...
        safeFill(s1aForm, 'form1[0].Page1[0].f1_12[0]', formatMoney(tCalc.line4c_1a || 0));
        safeFill(s1aForm, 'form1[0].Page1[0].f1_13[0]', formatMoney(parseFloat(formData.tipsSelfEmployed) || 0));
        safeFill(s1aForm, 'form1[0].Page1[0].f1_14[0]', formatMoney(tCalc.line6_1a || 0));
        safeFill(s1aForm, 'form1[0].Page1[0].f1_15[0]', formatMoney(tCalc.line7_1a || 0));

        // Phase-out (Lines 8-12)
        safeFill(s1aForm, 'form1[0].Page1[0].f1_16[0]', formatMoney(tCalc.line8_1a || 0));
        safeFill(s1aForm, 'form1[0].Page1[0].f1_17[0]', formatMoney(tCalc.line9_1a || 0));
        
        if ((tCalc.line10_1a || 0) > 0) {
            safeFill(s1aForm, 'form1[0].Page1[0].f1_18[0]', formatMoney(tCalc.line10_1a || 0));
            safeFill(s1aForm, 'form1[0].Page1[0].f1_19[0]', String(tCalc.line11_1a || 0));
            safeFill(s1aForm, 'form1[0].Page1[0].f1_20[0]', formatMoney(tCalc.line12_1a || 0));
        }
        
        // Line 13: Final
        safeFill(s1aForm, 'form1[0].Page1[0].f1_21[0]', formatMoney(tCalc.line13_1a || 0));


        // --- PART III: OVERTIME ---
        const oCalc = totals.sched1A.overtimeCalc || {};

        // Line 14a: W-2 Overtime
        // Note: Ensure your App.js calculation returns 'line14a_1a' or update this to match 'line14a'
        safeFill(s1aForm, 'form1[0].Page1[0].f1_22[0]', formatMoney(oCalc.line14a_1a || oCalc.line14a || 0));

        // Line 14b: 1099 Overtime
        safeFill(s1aForm, 'form1[0].Page1[0].f1_23[0]', formatMoney(oCalc.line14b_1a || oCalc.line14b || 0));

        safeFill(s1aForm, 'form1[0].Page1[0].f1_24[0]', formatMoney(oCalc.line14c_1a || 0));
        safeFill(s1aForm, 'form1[0].Page1[0].f1_25[0]', formatMoney(oCalc.line15_1a || 0));
        
        // Phase-out (Lines 16-20)
        safeFill(s1aForm, 'form1[0].Page1[0].f1_26[0]', formatMoney(oCalc.line16_1a || 0));
        safeFill(s1aForm, 'form1[0].Page1[0].f1_27[0]', formatMoney(oCalc.line17_1a || 0));

        if ((oCalc.line18_1a || 0) > 0) {
            safeFill(s1aForm, 'form1[0].Page1[0].f1_28[0]', formatMoney(oCalc.line18_1a || 0));
            safeFill(s1aForm, 'form1[0].Page1[0].f1_29[0]', String(oCalc.line19_1a || 0));
            safeFill(s1aForm, 'form1[0].Page1[0].f1_30[0]', formatMoney(oCalc.line20_1a || 0));
        }

        // Line 21: Final
        safeFill(s1aForm, 'form1[0].Page1[0].f1_31[0]', formatMoney(oCalc.line21_1a || 0));


        // --- PART IV: CAR LOAN ---
        const cCalc = totals.sched1A.carLoanCalc || {};
        const car1 = cCalc.car1 || {};
        const car2 = cCalc.car2 || {};

        // Line 22a (Vehicle 1)
        safeFill(s1aForm, 'form1[0].Page2[0].Table_Line22[0].Line22a[0].VIN-1_Comb[0].f2_01[0]', car1.vin || ''); // VIN
        safeFill(s1aForm, 'form1[0].Page2[0].Table_Line22[0].Line22a[0].f2_02[0]', formatMoney(car1.biz || 0)); // Col (ii)
        safeFill(s1aForm, 'form1[0].Page2[0].Table_Line22[0].Line22a[0].f2_03[0]', formatMoney(car1.net || 0)); // Col (iii)

        // Line 22b (Vehicle 2)
        if ((car2.net || 0) > 0 || car2.vin) {
            safeFill(s1aForm, 'form1[0].Page2[0].Table_Line22[0].Line22b[0].VIN-2_Comb[0].f2_04[0]', car2.vin || ''); // VIN
            safeFill(s1aForm, 'form1[0].Page2[0].Table_Line22[0].Line22b[0].f2_05[0]', formatMoney(car2.biz || 0)); // Col (ii)
            safeFill(s1aForm, 'form1[0].Page2[0].Table_Line22[0].Line22b[0].f2_06[0]', formatMoney(car2.net || 0)); // Col (iii)
        }

        safeFill(s1aForm, 'form1[0].Page2[0].f2_07[0]', formatMoney(cCalc.line23_1a || 0));
        safeFill(s1aForm, 'form1[0].Page2[0].f2_08[0]', formatMoney(cCalc.line24_1a || 0));

        // Phase-out (Lines 25-29)
        safeFill(s1aForm, 'form1[0].Page2[0].f2_09[0]', formatMoney(cCalc.line25_1a || 0));
        safeFill(s1aForm, 'form1[0].Page2[0].f2_10[0]', formatMoney(cCalc.line26_1a || 0));

        if ((cCalc.line27_1a || 0) > 0) {
            safeFill(s1aForm, 'form1[0].Page2[0].f2_11[0]', formatMoney(cCalc.line27_1a || 0));
            safeFill(s1aForm, 'form1[0].Page2[0].f2_12[0]', String(cCalc.line28_1a || 0));
            safeFill(s1aForm, 'form1[0].Page2[0].f2_13[0]', formatMoney(cCalc.line29_1a || 0));
        }

        // Line 30: Final
        safeFill(s1aForm, 'form1[0].Page2[0].f2_14[0]', formatMoney(cCalc.line30_1a || 0));


        // --- PART V: SENIORS ---
        const sCalc = totals.sched1A.seniorCalc || {};

        safeFill(s1aForm, 'form1[0].Page2[0].f2_15[0]', formatMoney(sCalc.line31_1a || 0));
        safeFill(s1aForm, 'form1[0].Page2[0].f2_16[0]', formatMoney(sCalc.line32_1a || 0));

        if ((sCalc.line33_1a || 0) > 0) {
            safeFill(s1aForm, 'form1[0].Page2[0].f2_17[0]', formatMoney(sCalc.line33_1a || 0));
            safeFill(s1aForm, 'form1[0].Page2[0].f2_18[0]', formatMoney(sCalc.line34_1a || 0));
        }

        // Line 35: Adjusted Base
        safeFill(s1aForm, 'form1[0].Page2[0].f2_19[0]', formatMoney(sCalc.line35_1a || 0));

        // Line 36a/b
        if (formData.isOver65) {
            safeFill(s1aForm, 'form1[0].Page2[0].f2_20[0]', formatMoney(sCalc.line35_1a || 0));
        }
        if (formData.filingStatus.includes('Married') && formData.spouseIsOver65) {
            safeFill(s1aForm, 'form1[0].Page2[0].f2_21[0]', formatMoney(sCalc.line35_1a || 0));
        }

        // Line 37: Final
        safeFill(s1aForm, 'form1[0].Page2[0].f2_22[0]', formatMoney(sCalc.line37_1a || 0));

        // --- PART VI: TOTAL ---
        safeFill(s1aForm, 'form1[0].Page2[0].f2_23[0]', formatMoney(totals.sched1A.total || 0));

        // 4. Flatten and Merge into Main PDF
        s1aForm.flatten();
        const s1aPages = await pdfDoc.copyPages(s1aDoc, s1aDoc.getPageIndices());
        s1aPages.forEach(page => pdfDoc.addPage(page));

    } catch (e) {
        console.error("Error generating Schedule 1-A:", e);
        alert("Could not load Schedule 1-A (f1040s1a.pdf). Please ensure it is in the public folder.");
    }
}

// ===========================================================================
    // SCHEDULE 2: ADDITIONAL TAXES (2024 Version)
    // ===========================================================================
    
    // Check if Schedule 2 is required based on Part I or Part II totals
    // ===========================================================================
    // SCHEDULE 2: ADDITIONAL TAXES (2025 Mappings)
    // ===========================================================================
    
    const isSchedule2Required = (totals.sched2_line3 > 0) || (totals.sched2_line21 > 0);
    console.log("Schedule 2 is required:", totals.sched2_line3, totals.sched2_line21);

    if (isSchedule2Required) {
        try {
            const s2Bytes = await fetch('/f1040s2.pdf').then(res => res.arrayBuffer());
            const s2Doc = await PDFDocument.load(s2Bytes);
            const s2Form = s2Doc.getForm();

            // --- Header ---
            const fullName = `${formData.firstName || ''} ${formData.lastName || ''}`.trim();
            safeFill(s2Form, 'form1[0].Page1[0].f1_01[0]', fullName);
            safeFill(s2Form, 'form1[0].Page1[0].f1_02[0]', formData.ssn || '');

            // ===========================================================================
            // PART I: TAX (Lines 1 - 3)
            // ===========================================================================
            
            // Line 1a: Excess advance premium tax credit repayment (Form 8962)
            safeFill(s2Form, 'form1[0].Page1[0].Line1a_ReadOrder[0].f1_03[0]', formatMoney(totals.sched2_line2 || 0)); 

            // Line 1b: Repayment of new clean vehicle credit (Form 8936)
            safeFill(s2Form, 'form1[0].Page1[0].f1_04[0]', formatMoney(totals.sched2_line1b || 0)); 

            // Line 1c: Repayment of previously owned clean vehicle credit (Form 8936)
            safeFill(s2Form, 'form1[0].Page1[0].f1_05[0]', formatMoney(totals.sched2_line1c || 0)); 

            // Line 1d: Recapture of net EPE from Form 4255
            safeFill(s2Form, 'form1[0].Page1[0].f1_06[0]', formatMoney(totals.sched2_line1d || 0)); 

            // Line 1e: Excessive payments (EP) from Form 4255
            safeFill(s2Form, 'form1[0].Page1[0].f1_07[0]', formatMoney(totals.sched2_line1e || 0)); 

            // Line 1f: 20% EP from Form 4255 (Placeholder)
            safeFill(s2Form, 'form1[0].Page1[0].f1_08[0]', formatMoney(0)); 

            // Line 1y: Other additions to tax
            // Note: Your list shows f1_09 is likely description (width 151) and f1_10 is amount (width 71)
            safeFill(s2Form, 'form1[0].Page1[0].f1_09[0]', formData.otherAdditionsTaxDescription || "");
            safeFill(s2Form, 'form1[0].Page1[0].f1_10[0]', formatMoney(totals.sched2_line1y || 0)); 

            // Line 1z: Add lines 1a through 1y
            safeFill(s2Form, 'form1[0].Page1[0].f1_11[0]', formatMoney(totals.sched2_line1z || 0)); 

            // Line 2: Alternative Minimum Tax (Form 6251)
            safeFill(s2Form, 'form1[0].Page1[0].f1_12[0]', formatMoney(totals.sched2_line1 || 0)); 

            // Line 3: Add lines 1z and 2 (Enters on 1040 Line 17)
            safeFill(s2Form, 'form1[0].Page1[0].f1_13[0]', formatMoney(totals.sched2_line3 || 0)); 


            safeFill(s2Form, 'form1[0].Page1[0].Line4_ReadOrder[0].f1_14[0]', formatMoney(totals.sched2_line4 || 0)); // 4
            safeFill(s2Form, 'form1[0].Page1[0].f1_15[0]', formatMoney(totals.sched2_line5 || 0)); // 5
            safeFill(s2Form, 'form1[0].Page1[0].f1_16[0]', formatMoney(totals.sched2_line6 || 0)); // 6
            safeFill(s2Form, 'form1[0].Page1[0].f1_17[0]', formatMoney(totals.sched2_line7 || 0)); // 7
            
            // Line 8
            safeFill(s2Form, 'form1[0].Page1[0].f1_18[0]', formatMoney(totals.sched2_line8 || 0)); 
            if (formData.suppressForm5329 !== false) {
                safeCheck(s2Form, 'form1[0].Page1[0].Line8_ReadOrder[0].c1_6[0]'); 
            }
            
            // Line 9: Household Employment
            safeFill(s2Form, 'form1[0].Page1[0].f1_19[0]', formatMoney(totals.sched2_line9 || 0)); 

            // Line 10: Homebuyer Credit
            safeFill(s2Form, 'form1[0].Page1[0].f1_20[0]', formatMoney(totals.sched2_line10 || 0)); 

            // Line 11: Additional Medicare Tax
            safeFill(s2Form, 'form1[0].Page1[0].f1_21[0]', formatMoney(totals.sched2_line11 || 0));

            // Line 12: Net Investment Income Tax
            safeFill(s2Form, 'form1[0].Page1[0].f1_22[0]', formatMoney(totals.sched2_line12 || 0));

            // Line 13: Uncollected W-2 Tax
            safeFill(s2Form, 'form1[0].Page1[0].f1_23[0]', formatMoney(totals.sched2_line13 || 0));

            // Line 14: Interest on installment sales (lots/timeshares)
            safeFill(s2Form, 'form1[0].Page1[0].f1_24[0]', formatMoney(totals.sched2_line14 || 0)); 

            // Line 15: Interest on deferred tax (> $150k)
            safeFill(s2Form, 'form1[0].Page1[0].f1_25[0]', formatMoney(totals.sched2_line15 || 0)); 

            // Line 16: Recapture of low-income housing credit (Form 8611)
            safeFill(s2Form, 'form1[0].Page1[0].f1_26[0]', formatMoney(totals.sched2_line16 || 0)); 

            // ===========================================================================
            // SCHEDULE 2: LINE 17 - OTHER ADDITIONAL TAXES (Page 2)
            // ===========================================================================

            const line17aItems = [];
                
            // Check for each possible type (you'll need to add these fields to your UI)
            if (formData.line17a_3468) {
                line17aItems.push(`3468 ${formData.line17a_3468_form || '4255'}`);
            }
            if (formData.line17a_NEPE3458) {
                line17aItems.push(`NEPE3458 ${formData.line17a_NEPE3458_form || '3468'}`);
            }
            if (formData.line17a_NMCR) {
                line17aItems.push('NMCR 8874');
            }
            if (formData.line17a_ECCFR) {
                line17aItems.push('ECCFR 8882');
            }
            if (formData.line17a_6418) {
                line17aItems.push('6418(g)(3) 4255');
            }
            
            // For now, if no specific items are checked, use the generic fields
            if (line17aItems.length === 0 && formData.line17aType && totals.sched2_line17a > 0) {
                line17aItems.push(`${formData.line17aType} ${formData.line17aFormNumber || ''}`.trim());
            }
            
            const description = line17aItems.join(', ');
            
            safeFill(s2Form, 'form1[0].Page2[0].Line17a_ReadOrder[0].Line17_ReadOrder[0].f2_01[0]', description || '');
            safeFill(s2Form, 'form1[0].Page2[0].Line17a_ReadOrder[0].f2_02[0]', formatMoney(totals.sched2_line17a || 0));

            // Line 17b: Tax on trust accumulation (Form 4970)
            safeFill(s2Form, 'form1[0].Page2[0].f2_03[0]', formatMoney(totals.sched2_line17b || 0));

            // Line 17c: Excise tax on insider stock (Form 7248)
            safeFill(s2Form, 'form1[0].Page2[0].f2_04[0]', formatMoney(totals.sched2_line17c || 0));

            // Line 17d: Look-back interest (Form 8697)
            safeFill(s2Form, 'form1[0].Page2[0].f2_05[0]', formatMoney(totals.sched2_line17d || 0));

            // Line 17e: Look-back interest (Form 8866)
            safeFill(s2Form, 'form1[0].Page2[0].f2_06[0]', formatMoney(totals.sched2_line17e || 0));

            // Line 17f: Uncollected Social Security and Medicare tax (Form 4137)
            safeFill(s2Form, 'form1[0].Page2[0].f2_07[0]', formatMoney(totals.sched2_line17f || 0));

            // Line 17g: Uncollected Social Security and Medicare/RRTA (Form 8919)
            safeFill(s2Form, 'form1[0].Page2[0].f2_08[0]', formatMoney(totals.sched2_line17g || 0));

            // Line 17h: Additional tax on IRAs, other qualified plans (Form 5329)
            safeFill(s2Form, 'form1[0].Page2[0].f2_09[0]', formatMoney(totals.sched2_line17h || 0));

            // Line 17i: Household employment taxes (Schedule H)
            safeFill(s2Form, 'form1[0].Page2[0].f2_10[0]', formatMoney(totals.sched2_line17i || 0));

            // Line 17j: Repayment of first-time homebuyer credit (Form 5405)
            safeFill(s2Form, 'form1[0].Page2[0].f2_11[0]', formatMoney(totals.sched2_line17j || 0));

            // Line 17k: Golden parachute payments
            safeFill(s2Form, 'form1[0].Page2[0].f2_12[0]', formatMoney(totals.sched2_line17k || 0));

            // Line 17l: Additional Medicare Tax (Form 8959) - AUTO-CALCULATED
            safeFill(s2Form, 'form1[0].Page2[0].f2_13[0]', formatMoney(totals.autoCalculatedAdditionalMedicareTax || 0));

            // Line 17m: Net Investment Income Tax (Form 8960) - AUTO-CALCULATED
            safeFill(s2Form, 'form1[0].Page2[0].f2_14[0]', formatMoney(totals.autoCalculatedNetInvestmentIncomeTax || 0));

            // Line 17n: Section 965 tax (transition tax)
            safeFill(s2Form, 'form1[0].Page2[0].f2_15[0]', formatMoney(totals.sched2_line17n || 0));

            // Line 17o: Section 951A tax (GILTI)
            safeFill(s2Form, 'form1[0].Page2[0].f2_16[0]', formatMoney(totals.sched2_line17o || 0));

            // Line 17p: Section 461(l) excess business loss
            safeFill(s2Form, 'form1[0].Page2[0].f2_17[0]', formatMoney(totals.sched2_line17p || 0));

            // Line 17q: Section 1061(a) deferred tax
            safeFill(s2Form, 'form1[0].Page2[0].f2_18[0]', formatMoney(totals.sched2_line17q || 0));

            const line17zItems = [];
                
            // Check for each possible type (you'll need to add these fields to your UI)
            if (formData.line17z_PWA7210) {
                line17zItems.push('PWA7210');
            }
            if (formData.line17z_PWA8933) {
                line17zItems.push('PWA8933');
            }
            if (formData.line17z_NPWA) {
                line17zItems.push('NPWA');
            }
            if (formData.line17z_Form8978) {
                line17zItems.push('Form 8978');
            }
            
            // For now, if no specific items are checked, use the generic field
            if (line17zItems.length === 0 && formData.line17zType && totals.sched2_line17z > 0) {
                line17zItems.push(formData.line17zType);
            }
            
            const description3 = line17zItems.join(', ');
            
            safeFill(s2Form, 'form1[0].Page2[0].Line17z_ReadOrder[0].f2_19[0]', description3 || '');
            safeFill(s2Form, 'form1[0].Page2[0].f2_20[0]', formatMoney(totals.sched2_line17z || 0));

            // Line 18: Total additional taxes (Sum of 17a-17z)
            safeFill(s2Form, 'form1[0].Page2[0].f2_21[0]', formatMoney(totals.sched2_line18 || 0));
            
            // Line 19: Additional tax from Schedule 8812
            safeFill(s2Form, 'form1[0].Page2[0].f2_22[0]', formatMoney(totals.sched2_line19)); 

            // Line 20: Section 965 net tax liability installment
            safeFill(s2Form, 'form1[0].Page2[0].f2_23[0]', formatMoney(totals.sched2_line20)); 

            // Line 21: Total Part II
            safeFill(s2Form, 'form1[0].Page2[0].f2_24[0]', formatMoney(totals.sched2_line21));

            s2Form.flatten();
            const s2Pages = await pdfDoc.copyPages(s2Doc, s2Doc.getPageIndices());
            s2Pages.forEach(page => pdfDoc.addPage(page));
        } catch (e) {
            console.error("Error loading Schedule 2:", e);
        }
    }

    // ===========================================================================
    // SCHEDULE 3: ADDITIONAL CREDITS (2025 Mappings)
    // ===========================================================================

    const isSchedule3Required = (totals.sched3_line8 > 0) || (totals.sched3_line15 > 0);

    if (isSchedule3Required) {
        try {
            const s3Bytes = await fetch('/f1040s3.pdf').then(res => res.arrayBuffer());
            const s3Doc = await PDFDocument.load(s3Bytes);
            const s3Form = s3Doc.getForm();
            const fullName = `${formData.firstName || ''} ${formData.lastName || ''}`.trim();

            // --- HEADER ---
            safeFill(s3Form, 'topmostSubform[0].Page1[0].f1_01[0]', fullName);
            safeFill(s3Form, 'topmostSubform[0].Page1[0].f1_02[0]', formData.ssn || '');

            // --- PART I: Nonrefundable Credits ---
            safeFill(s3Form, 'topmostSubform[0].Page1[0].f1_03[0]', formatMoney(totals.sched3_line1)); // Line 1 (Foreign Tax)
            safeFill(s3Form, 'topmostSubform[0].Page1[0].f1_04[0]', formatMoney(totals.sched3_line2)); // Line 2 (Child Care)
            safeFill(s3Form, 'topmostSubform[0].Page1[0].f1_05[0]', formatMoney(totals.sched3_line3)); // Line 3 (Education)
            safeFill(s3Form, 'topmostSubform[0].Page1[0].f1_06[0]', formatMoney(totals.sched3_line4)); // Line 4 (Retirement)
            safeFill(s3Form, 'topmostSubform[0].Page1[0].f1_07[0]', formatMoney(totals.sched3_line5a)); // Line 5a (Clean Energy)
            safeFill(s3Form, 'topmostSubform[0].Page1[0].f1_08[0]', formatMoney(totals.sched3_line5b)); // Line 5b (Energy Efficient Home)
            
            // Line 6 Breakdown
            safeFill(s3Form, 'topmostSubform[0].Page1[0].Line6a_ReadOrder[0].f1_09[0]', formatMoney(totals.sched3_line6a)); // 6a (General Biz)
            safeFill(s3Form, 'topmostSubform[0].Page1[0].f1_10[0]', formatMoney(totals.sched3_line6b)); // 6b (Prior Year Min Tax)
            safeFill(s3Form, 'topmostSubform[0].Page1[0].f1_11[0]', formatMoney(totals.sched3_line6c)); // 6c (Adoption)
            safeFill(s3Form, 'topmostSubform[0].Page1[0].f1_12[0]', formatMoney(totals.sched3_line6d)); // 6d (Elderly/Disabled)
            // 6e is reserved
            safeFill(s3Form, 'topmostSubform[0].Page1[0].f1_13[0]', formatMoney(totals.sched3_line6f)); // 6f (Clean Vehicle)
            safeFill(s3Form, 'topmostSubform[0].Page1[0].f1_14[0]', formatMoney(totals.sched3_line6g)); // 6g (Mortgage Interest)
            safeFill(s3Form, 'topmostSubform[0].Page1[0].f1_15[0]', formatMoney(totals.sched3_line6h)); // 6h (DC Homebuyer)
            safeFill(s3Form, 'topmostSubform[0].Page1[0].f1_16[0]', formatMoney(totals.sched3_line6i)); // 6i (Qualified Electric Vehicle)
            safeFill(s3Form, 'topmostSubform[0].Page1[0].f1_17[0]', formatMoney(totals.sched3_line6j)); // 6j (Alt Fuel Vehicle)
            safeFill(s3Form, 'topmostSubform[0].Page1[0].f1_18[0]', formatMoney(totals.sched3_line6k)); // 6k (Tax Credit Bonds)
            safeFill(s3Form, 'topmostSubform[0].Page1[0].f1_19[0]', formatMoney(totals.sched3_line6l)); // 6l (Form 8978)
            safeFill(s3Form, 'topmostSubform[0].Page1[0].f1_20[0]', formatMoney(totals.sched3_line6m)); // 6m (Previously Owned Clean Vehicle)
            
            // Line 6z: Other
            const line6zItems = [];
            if (formData.line6z_8910) line6zItems.push('Form 8910');
            if (formData.line6z_other) line6zItems.push(formData.line6z_other_description || 'Other');
            if (line6zItems.length === 0 && formData.line6zDescription) {
                line6zItems.push(formData.line6zDescription);
            }
            const desc6z = line6zItems.join(', ');

            safeFill(s3Form, 'topmostSubform[0].Page1[0].Line6z_ReadOrder[0].f2_22[0]', desc6z); // Desc
            safeFill(s3Form, 'topmostSubform[0].Page1[0].f1_23[0]', formatMoney(totals.sched3_line6z)); // Amount

            safeFill(s3Form, 'topmostSubform[0].Page1[0].f1_24[0]', formatMoney(totals.sched3_line7)); // Line 7 Total
            safeFill(s3Form, 'topmostSubform[0].Page1[0].f1_25[0]', formatMoney(totals.sched3_line8)); // Line 8 Total

            // --- PART II: Refundable Credits ---
            safeFill(s3Form, 'topmostSubform[0].Page1[0].f1_26[0]', formatMoney(totals.sched3_line9)); // Line 9 (Net PTC)
            safeFill(s3Form, 'topmostSubform[0].Page1[0].f1_27[0]', formatMoney(totals.sched3_line10)); // Line 10 (Paid w/ Extension)
            safeFill(s3Form, 'topmostSubform[0].Page1[0].f1_28[0]', formatMoney(totals.sched3_line11)); // Line 11 (Excess SS)
            safeFill(s3Form, 'topmostSubform[0].Page1[0].f1_29[0]', formatMoney(totals.sched3_line12)); // Line 12 (Fuel Tax)

            // Line 13 Breakdown
            safeFill(s3Form, 'topmostSubform[0].Page1[0].Line13_ReadOrder[0].f1_30[0]', formatMoney(totals.sched3_line13a)); // 13a (Form 2439)
            safeFill(s3Form, 'topmostSubform[0].Page1[0].f1_31[0]', formatMoney(totals.sched3_line13b)); // 13b (Section 1341)
            safeFill(s3Form, 'topmostSubform[0].Page1[0].f1_32[0]', formatMoney(totals.sched3_line13c)); // 13c (Elective Payment)
            safeFill(s3Form, 'topmostSubform[0].Page1[0].f1_33[0]', formatMoney(totals.sched3_line13d)); // 13d (Deferred 965)

            // Line 13z: Other
            const line13zItems = [];
            if (formData.line13z_960c) line13zItems.push('960(c)');
            if (formData.line13z_8689) line13zItems.push('Form 8689');
            if (formData.line13z_other) line13zItems.push(formData.line13z_other_description || 'Other');
            if (line13zItems.length === 0 && formData.line13zDescription) {
                line13zItems.push(formData.line13zDescription);
            }
            const desc13z = line13zItems.join(', ');

            safeFill(s3Form, 'topmostSubform[0].Page1[0].Line13z_ReadOrder[0].f1_34[0]', desc13z); // Desc
            safeFill(s3Form, 'topmostSubform[0].Page1[0].f1_35[0]', formatMoney(totals.sched3_line13z)); // Amount

            safeFill(s3Form, 'topmostSubform[0].Page1[0].f1_36[0]', formatMoney(totals.sched3_line14)); // Line 14 Total
            safeFill(s3Form, 'topmostSubform[0].Page1[0].f1_37[0]', formatMoney(totals.sched3_line15)); // Line 15 Total

            s3Form.flatten();
            const s3Pages = await pdfDoc.copyPages(s3Doc, s3Doc.getPageIndices());
            s3Pages.forEach(page => pdfDoc.addPage(page));
        } catch (e) {
            console.error("Error loading Schedule 3:", e);
        }
    }
console.log("deductions are: ", totals.deductionType);
console.log("Deduction Type Received:", totals.deductionType);
  console.log("Standard Deduction Amount:", totals.standardDeduction);
  console.log("Itemized Deduction Amount:", totals.totalItemizedDeductions);
  console.log("Final Line 12 Amount:", totals.line12);
if (totals.deductionType === 'Itemized') {
        try {
            console.log("Generating Schedule A...");
            const saBytes = await fetch('/f1040sa.pdf').then(res => res.arrayBuffer());
            const saDoc = await PDFDocument.load(saBytes);
            const saForm = saDoc.getForm();

            // Header
            const fullName = `${formData.firstName || ''} ${formData.lastName || ''}`.trim();
            safeFill(saForm, 'form1[0].Page1[0].f1_1[0]', fullName);
            safeFill(saForm, 'form1[0].Page1[0].f1_2[0]', formData.ssn || '');

            const itemized = formData.itemizedDeductions;

            // --- Medical Expenses (Lines 1-4) ---
            const medExpenses = parseFloat(itemized.medicalExpenses) || 0;
            const agi = totals.agi || 0;
            const medThreshold = agi * 0.075;
            const medDeduction = Math.max(0, medExpenses - medThreshold);

            safeFill(saForm, 'form1[0].Page1[0].f1_3[0]', formatMoney(medExpenses)); // Line 1
            safeFill(saForm, 'form1[0].Page1[0].Line2_ReadOrder[0].f1_4[0]', formatMoney(agi)); // Line 2
            safeFill(saForm, 'form1[0].Page1[0].f1_5[0]', formatMoney(medThreshold)); // Line 3
            safeFill(saForm, 'form1[0].Page1[0].f1_6[0]', formatMoney(medDeduction)); // Line 4

            // --- Taxes You Paid (Lines 5-7) ---
            // Use the pre-calculated winner from App.js
            const line5aAmount = totals.line5aAmount || 0; 
            const realEstate = parseFloat(itemized.realEstateTaxes) || 0;
            const personalProp = parseFloat(itemized.personalPropertyTaxes) || 0;
            const otherTaxes = parseFloat(itemized.otherTaxes) || 0;
            
            // Checkbox 5a: Check ONLY if electing Sales Tax
            if (totals.isElectingSalesTax) {
                 safeCheck(saForm, 'form1[0].Page1[0].c1_1[0]'); 
            }
            
            safeFill(saForm, 'form1[0].Page1[0].f1_7[0]', formatMoney(line5aAmount)); // Line 5a
            safeFill(saForm, 'form1[0].Page1[0].f1_8[0]', formatMoney(realEstate)); // Line 5b
            safeFill(saForm, 'form1[0].Page1[0].f1_9[0]', formatMoney(personalProp)); // Line 5c
            
            // Line 5d: Total SALT
            const totalSalt = line5aAmount + realEstate + personalProp;
            safeFill(saForm, 'form1[0].Page1[0].f1_10[0]', formatMoney(totalSalt)); 

            // Line 5e: SALT Cap Calculation
            const saltCap = formData.filingStatus === 'Married Filing Separately' ? 5000 : 10000;
            const allowedSalt = Math.min(totalSalt, saltCap);
            safeFill(saForm, 'form1[0].Page1[0].f1_11[0]', formatMoney(allowedSalt)); 

            const line6Items = [];
            if (itemized.otherTaxesDescription) {
                line6Items.push(itemized.otherTaxesDescription);
            }

            // 2. Use the helper to split long text across two lines (limit ~35 chars)
            const { line1: line6Desc1, line2: line6Desc2 } = splitItemsAcrossLines(line6Items, 15);

            // 3. Fill the PDF fields
            // Line 6 Description (Row 1)
            safeFill(saForm, 'form1[0].Page1[0].f1_12[0]', line6Desc1 || ''); 
            
            // Line 6 Description (Row 2) - 2025 form only has 1 line for description
            // safeFill(saForm, 'form1[0].Page1[0].f1_13[0]', line6Desc2 || ''); 

            safeFill(saForm, 'form1[0].Page1[0].f1_13[0]', formatMoney(otherTaxes)); // Amount (f1_13 is now Amount)

            // Line 7: Total Taxes (5e + 6)
            const totalTaxesDeduction = allowedSalt + otherTaxes;
            safeFill(saForm, 'form1[0].Page1[0].f1_14[0]', formatMoney(totalTaxesDeduction));

            // --- Interest You Paid (Lines 8-10) ---
            const mortgageInt = parseFloat(itemized.mortgageInterest) || 0;
            const mortgageIntNot1098 = parseFloat(itemized.mortgageInterestNot1098) || 0;
            const points = parseFloat(itemized.pointsNot1098) || 0;
            const mortgagePrem = parseFloat(itemized.mortgageInsurancePremiums) || 0;
            const investInt = parseFloat(itemized.investmentInterest) || 0;

            if (itemized.mortgageProceedsNotUsedForHome) {
                safeCheck(saForm, 'form1[0].Page1[0].Line8_ReadOrder[0].c1_2[0]'); 
            }
            
            safeFill(saForm, 'form1[0].Page1[0].f1_15[0]', formatMoney(mortgageInt)); // Line 8a
            
            const payeeInfo = itemized.mortgageInterestPayee || '';
            
            // 2. Split it intelligently across the two lines provided on the form
            const { line1: payeeLine1, line2: payeeLine2 } = splitItemsAcrossLines([payeeInfo], 40);

            // 3. Fill the PDF fields (Row 1 and Row 2)
            safeFill(saForm, 'form1[0].Page1[0].Line8b_ReadOrder[0].f1_16[0]', payeeLine1); 
            // safeFill(saForm, 'form1[0].Page1[0].f1_18[0]', payeeLine2); // 2025 form only has 1 line for description
            safeFill(saForm, 'form1[0].Page1[0].f1_17[0]', formatMoney(mortgageIntNot1098)); // Amount (f1_17 is now Amount)

            safeFill(saForm, 'form1[0].Page1[0].f1_18[0]', formatMoney(points)); // Line 8c
            // Line 8d empty
            safeFill(saForm, 'form1[0].Page1[0].f1_19[0]', formatMoney(mortgagePrem)); // Line 8d
            
            // Line 8e: Total Mortgage Interest
            const totalMortgageInterest = mortgageInt + mortgageIntNot1098 + points + mortgagePrem;
            safeFill(saForm, 'form1[0].Page1[0].f1_20[0]', formatMoney(totalMortgageInterest));

            // Line 9: Investment Interest
            safeFill(saForm, 'form1[0].Page1[0].f1_21[0]', formatMoney(investInt));

            // Line 10: Total Interest (8e + 9)
            const totalInterestDeduction = totalMortgageInterest + investInt;
            safeFill(saForm, 'form1[0].Page1[0].f1_22[0]', formatMoney(totalInterestDeduction));

            // --- Gifts to Charity (Lines 11-14) ---
            
            const cashCharity = totals.deductibleCashCharity || 0; 
            
            // UPDATED: Use the calculated deductible amount for non-cash
            const nonCashCharity = totals.deductibleNonCashCharity || 0;
            
            const carryoverCharity = parseFloat(itemized.charitableCarryover) || 0;
            
            safeFill(saForm, 'form1[0].Page1[0].f1_23[0]', formatMoney(cashCharity)); // Line 11
            safeFill(saForm, 'form1[0].Page1[0].f1_24[0]', formatMoney(nonCashCharity)); // Line 12 (Updated)
            safeFill(saForm, 'form1[0].Page1[0].f1_25[0]', formatMoney(carryoverCharity)); // Line 13
            
            // Line 14: Total Charity
            const totalCharityDeduction = cashCharity + nonCashCharity + carryoverCharity;
            safeFill(saForm, 'form1[0].Page1[0].f1_26[0]', formatMoney(totalCharityDeduction)); 

            const casualtyLoss = totals.deductibleCasualtyLoss || 0;
            
            safeFill(saForm, 'form1[0].Page1[0].f1_27[0]', formatMoney(casualtyLoss)); 

            // --- Line 16: Other Itemized Deductions ---
            
            // 1. Build the description string
            const l16Items = [];
            const l16Details = itemized.line16Details || {};
            
            if (totals.deductibleGamblingLosses > 0) l16Items.push(`Gambling Losses $${formatMoney(totals.deductibleGamblingLosses)}`);
            if (totals.disasterLossL16 > 0) l16Items.push(`Net Qualified Disaster Loss $${formatMoney(totals.disasterLossL16)}`);
            if (parseFloat(l16Details.impairmentWorkExpenses) > 0) l16Items.push(`Impairment Exp $${formatMoney(l16Details.impairmentWorkExpenses)}`);
            if (parseFloat(l16Details.claimOfRightRepayment) > 0) l16Items.push(`Claim of Right $${formatMoney(l16Details.claimOfRightRepayment)}`);
            if (parseFloat(l16Details.unrecoveredPension) > 0) l16Items.push(`Unrecovered Pension $${formatMoney(l16Details.unrecoveredPension)}`);
            if (parseFloat(l16Details.otherMiscAmount) > 0) {
                const desc = l16Details.otherMiscDescription || 'Other';
                l16Items.push(`${desc} $${formatMoney(l16Details.otherMiscAmount)}`);
            }

            // 2. Custom Split Logic (15 chars line 1, 50 chars lines 2 & 3)
            const fullText = l16Items.join(', ');
            let line1 = '', line2 = '', line3 = '';
            
            const limitLine1 = 15;
            const limitLine2 = 50;

            // --- Calculate Line 1 ---
            if (fullText.length <= limitLine1) {
                line1 = fullText;
            } else {
                // Find split point for Line 1 (look backwards from char 15)
                let split1 = fullText.lastIndexOf(',', limitLine1);
                if (split1 === -1) split1 = fullText.lastIndexOf(' ', limitLine1);
                if (split1 === -1) split1 = limitLine1; // Hard split if no spaces found
                
                line1 = fullText.substring(0, split1 + 1).trim();
                const remaining1 = fullText.substring(split1 + 1).trim();
                
                // --- Calculate Line 2 ---
                if (remaining1.length <= limitLine2) {
                    line2 = remaining1;
                } else {
                    // Find split point for Line 2 (look backwards from char 50)
                    let split2 = remaining1.lastIndexOf(',', limitLine2);
                    if (split2 === -1) split2 = remaining1.lastIndexOf(' ', limitLine2);
                    if (split2 === -1) split2 = limitLine2;
                    
                    line2 = remaining1.substring(0, split2 + 1).trim();
                    
                    // --- Calculate Line 3 ---
                    // Everything else goes to Line 3 (trimmed)
                    line3 = remaining1.substring(split2 + 1).trim(); 
                }
            }

            // 3. Fill PDF (Rows 1, 2, and 3)
            safeFill(saForm, 'form1[0].Page1[0].f1_28[0]', line1); 
            // safeFill(saForm, 'topmostSubform[0].Page1[0].f1_31[0]', line2); // 2025 form only has 1 line for description
            // safeFill(saForm, 'topmostSubform[0].Page1[0].f1_32[0]', line3); // 2025 form only has 1 line for description
            
            safeFill(saForm, 'form1[0].Page1[0].f1_29[0]', formatMoney(totals.line16Total)); // Total Amount

            // --- Total Itemized Deductions (Line 17) ---
            // We use the calculated total from App.js to ensure consistency
            safeFill(saForm, 'form1[0].Page1[0].f1_30[0]', formatMoney(totals.finalDeduction)); 

            if (totals.forceItemized && (totals.totalItemizedDeductions < totals.standardDeduction)) {
                 safeCheck(saForm, 'form1[0].Page1[0].Line18_ReadOrder[0].c1_3[0]'); 
            }
            
            saForm.flatten();
            const saPages = await pdfDoc.copyPages(saDoc, saDoc.getPageIndices());
            saPages.forEach(page => pdfDoc.addPage(page));

        } catch (e) {
            console.error("Error loading Schedule A:", e);
            alert("Could not load Schedule A (f1040sa.pdf). Ensure it is in the public folder.");
        }
    }

    // src/utils/pdfGenerator.js

// ... inside generate1040 function ...

// ===========================================================================
    // SCHEDULE B: INTEREST & DIVIDENDS (2025 Mappings)
    // ===========================================================================

    const interestPayers = formData.interestPayers || [];
    const dividendPayers = formData.dividendPayers || [];
    const totalInterest = parseFloat(formData.taxableInterest) || 0;
    const totalDividends = parseFloat(formData.ordinaryDividends) || 0;

    const isScheduleBRequired = totalInterest > 1500 || totalDividends > 1500 || formData.foreignAccounts;

    if (isScheduleBRequired) {
        try {
            const sbBytes = await fetch('/f1040sb.pdf').then(res => res.arrayBuffer());
            const sbDoc = await PDFDocument.load(sbBytes);
            const sbForm = sbDoc.getForm();

            // Header
            const fullName = `${formData.firstName || ''} ${formData.lastName || ''}`.trim();
            safeFill(sbForm, 'topmostSubform[0].Page1[0].f1_01[0]', fullName);
            safeFill(sbForm, 'topmostSubform[0].Page1[0].f1_02[0]', formData.ssn || '');

            // --- PART I: INTEREST ---
            // The new form uses sequential IDs:
            // Row 1: Name=f1_03, Amount=f1_04
            // Row 2: Name=f1_05, Amount=f1_06
            // ... up to Row 14: Name=f1_29, Amount=f1_30
            
            interestPayers.slice(0, 14).forEach((payer, index) => {
                // Calculate IDs based on index
                // Base for Row 1 is 3. Step is 2.
                const nameId = 3 + (index * 2);
                const amtId = 4 + (index * 2);
                
                // Construct field names
                // Note: Row 1 has a special wrapper "Line1_ReadOrder" in your list
                let nameField, amtField;
                
                if (index === 0) {
                    nameField = 'topmostSubform[0].Page1[0].Line1_ReadOrder[0].f1_03[0]';
                } else {
                    nameField = `topmostSubform[0].Page1[0].f1_${String(nameId).padStart(2, '0')}[0]`;
                }
                amtField = `topmostSubform[0].Page1[0].f1_${String(amtId).padStart(2, '0')}[0]`;

                // Format Description & Amount
                let description = payer.name;
                let formattedAmount = formatMoney(payer.amount);

                if (payer.type === 'SellerFinanced') {
                    const parts = [payer.name, payer.address, payer.ssn].filter(Boolean);
                    description = parts.join(', ');
                } 
                else if (['Nominee', 'AccruedInterest', 'OID', 'ABP'].includes(payer.type)) {
                    formattedAmount = `(${formatMoney(payer.amount)})`;
                    if (payer.type === 'Nominee') description = `Nominee Distribution: ${payer.name}`;
                    else if (payer.type === 'AccruedInterest') description = `Accrued Interest: ${payer.name}`;
                    else if (payer.type === 'OID') description = `OID Adjustment: ${payer.name}`;
                    else if (payer.type === 'ABP') description = `ABP Adjustment: ${payer.name}`;
                }

                safeFill(sbForm, nameField, description);
                safeFill(sbForm, amtField, formattedAmount);
            });

            // Line 2: Total Interest
            // Sum of amounts listed above
            const line2_Total = interestPayers.reduce((sum, item) => {
                const amt = parseFloat(item.amount) || 0;
                if (['Nominee', 'AccruedInterest', 'OID', 'ABP'].includes(item.type)) return sum - amt;
                return sum + amt;
            }, 0);
            safeFill(sbForm, 'topmostSubform[0].Page1[0].f1_31[0]', formatMoney(line2_Total));

            // Line 3: Excludable Savings Bond Interest
            const line3_Excludable = parseFloat(formData.excludableSavingsBondInterest) || 0;
            if (line3_Excludable > 0) {
                safeFill(sbForm, 'topmostSubform[0].Page1[0].f1_32[0]', formatMoney(line3_Excludable));
            }

            // Line 4: Net Taxable Interest
            const line4_Taxable = Math.max(0, line2_Total - line3_Excludable);
            safeFill(sbForm, 'topmostSubform[0].Page1[0].f1_33[0]', formatMoney(line4_Taxable));


            // --- PART II: DIVIDENDS ---
            // Row 1: Name=f1_34, Amount=f1_35 (Special Wrapper)
            // Row 2: Name=f1_36, Amount=f1_37
            // ... up to Row 15: Name=f1_62, Amount=f1_63
            
            dividendPayers.slice(0, 15).forEach((payer, index) => {
                // Base for Row 1 is 34. Step is 2.
                const nameId = 34 + (index * 2);
                const amtId = 35 + (index * 2);
                
                let nameField, amtField;

                if (index === 0) {
                    nameField = 'topmostSubform[0].Page1[0].ReadOrderControl[0].f1_34[0]';
                } else {
                    nameField = `topmostSubform[0].Page1[0].f1_${String(nameId).padStart(2, '0')}[0]`;
                }
                amtField = `topmostSubform[0].Page1[0].f1_${String(amtId).padStart(2, '0')}[0]`;

                let description = payer.name;
                let formattedAmount = formatMoney(payer.amount);

                if (payer.type === 'Nominee') {
                    description = `Nominee Distribution: ${payer.name}`;
                    formattedAmount = `(${formatMoney(payer.amount)})`;
                }

                safeFill(sbForm, nameField, description);
                safeFill(sbForm, amtField, formattedAmount);
            });

            // Line 6: Total Ordinary Dividends
            safeFill(sbForm, 'topmostSubform[0].Page1[0].f1_64[0]', formatMoney(totalDividends));


            // --- PART III: FOREIGN ACCOUNTS ---
            
            // Line 7a: Foreign Accounts?
            if (formData.foreignAccounts) {
                safeCheck(sbForm, 'topmostSubform[0].Page1[0].TagcorrectingSubform[0].c1_1[0]'); // Yes
                
                // Line 7a (Part 2): Required to file FBAR?
                if (formData.fileFbar) {
                    safeCheck(sbForm, 'topmostSubform[0].Page1[0].c1_2[0]'); // Yes
                    
                    // Line 7b: Country Name
                    // Note: Your list shows f1_65 and f1_66 for this text
                    const countryText = formData.foreignAccountCountry || '';
                    const { line1, line2 } = splitItemsAcrossLines([countryText], 40);
                    
                    safeFill(sbForm, 'topmostSubform[0].Page1[0].f1_65[0]', line1);
                    safeFill(sbForm, 'topmostSubform[0].Page1[0].f1_66[0]', line2);
                } else {
                    safeCheck(sbForm, 'topmostSubform[0].Page1[0].c1_2[1]'); // No
                }
            } else {
                safeCheck(sbForm, 'topmostSubform[0].Page1[0].TagcorrectingSubform[0].c1_1[1]'); // No
            }

            // Line 8: Foreign Trust?
            if (formData.foreignTrust) {
                safeCheck(sbForm, 'topmostSubform[0].Page1[0].c1_3[0]'); // Yes
            } else {
                safeCheck(sbForm, 'topmostSubform[0].Page1[0].c1_3[1]'); // No
            }

            sbForm.flatten();
            const sbPages = await pdfDoc.copyPages(sbDoc, sbDoc.getPageIndices());
            sbPages.forEach(page => pdfDoc.addPage(page));

        } catch (e) {
            console.error("Error generating Schedule B:", e);
        }
    }

// --- SCHEDULE C: PROFIT OR LOSS FROM BUSINESS ---
// ===========================================================================
    // SCHEDULE C: PROFIT OR LOSS FROM BUSINESS
    // ===========================================================================

    const scheduleCData = formData.scheduleCData || [];

    console.log("DEBUG 3: Generator received Schedule C Data:", scheduleCData);

    const hasBusinessActivity = scheduleCData.some(item => {
        // Debug: Log specific values as it checks each business
        console.log(
            "Checking Business:", 
            "Name=", item.businessName, 
            "Receipts=", item.grossReceipts, 
            "Ads=", item.advertising
        );

        // Return true if any of these exist
        return (
            (item.businessName && item.businessName.trim() !== '') || 
            (parseFloat(item.grossReceipts) || 0) > 0 || 
            (parseFloat(item.advertising) || 0) > 0 ||
            (parseFloat(item.carAndTruckExpenses) || 0) > 0
        );
    });

    console.log("Has Business Activity?", hasBusinessActivity);

    if (hasBusinessActivity) {
        try {
            console.log("Generating Schedule C...");
            const scBytes = await fetch('/f1040sc.pdf').then(res => res.arrayBuffer());
            
            // --- LOOP STARTS HERE ---
            // We iterate through every business in the array. 
            // 'biz' is defined ONLY inside this loop.
            for (const biz of scheduleCData) {
                
                // Load a fresh copy of the form for this specific business
                const tempDoc = await PDFDocument.load(scBytes);
                const scForm = tempDoc.getForm();

                console.log("DEBUG 4: Processing Business:", biz);
                console.log("DEBUG 5: Gross Receipts Raw Value:", biz.grossReceipts);

                // --- HEADER INFO ---
                safeFill(scForm, 'topmostSubform[0].Page1[0].f1_1[0]', `${formData.firstName} ${formData.lastName}`);
                safeFill(scForm, 'topmostSubform[0].Page1[0].f1_2[0]', formData.ssn);
                
                safeFill(scForm, 'topmostSubform[0].Page1[0].f1_3[0]', biz.principalBusiness); // Line A
                safeFill(scForm, 'topmostSubform[0].Page1[0].BComb[0].f1_4[0]', biz.code); // Line B
                safeFill(scForm, 'topmostSubform[0].Page1[0].f1_5[0]', biz.businessName); // Line C
                safeFill(scForm, 'topmostSubform[0].Page1[0].DComb[0].f1_6[0]', biz.ein); // Line D
                safeFill(scForm, 'topmostSubform[0].Page1[0].f1_7[0]', biz.businessAddress); // Line E
                
                const combinedCityStateZip = `${biz.businessCity || ''}, ${biz.businessState || ''} ${biz.businessZip || ''}`.trim();
                // Remove the leading comma if city is missing
                const finalCityStateZip = combinedCityStateZip.startsWith(',') ? combinedCityStateZip.substring(1).trim() : combinedCityStateZip;
                
                safeFill(scForm, 'topmostSubform[0].Page1[0].f1_8[0]', finalCityStateZip); 

                // Line F: Accounting Method
                if (biz.accountingMethod === 'Cash') {
                    safeCheck(scForm, 'topmostSubform[0].Page1[0].c1_1[0]');
                } else if (biz.accountingMethod === 'Accrual') {
                    safeCheck(scForm, 'topmostSubform[0].Page1[0].c1_1[1]');
                } else {
                    // Other
                    safeCheck(scForm, 'topmostSubform[0].Page1[0].c1_1[2]');
                    // Specify the method (Line F, number 3)
                    // FIX: Use the specific description field
                    safeFill(scForm, 'topmostSubform[0].Page1[0].f1_9[0]', biz.accountingMethodOtherDescription || 'Other'); 
                }

                // Line G: Material Participation
                if (biz.materiallyParticipated) safeCheck(scForm, 'topmostSubform[0].Page1[0].c1_2[0]'); // Yes
                else safeCheck(scForm, 'topmostSubform[0].Page1[0].c1_2[1]'); // No

                // Line H: Started in 2024?
                if (biz.materiallyParticipated) safeCheck(scForm, 'topmostSubform[0].Page1[0].c1_2[0]'); // Yes
                else safeCheck(scForm, 'topmostSubform[0].Page1[0].c1_2[1]'); // No

                // Line H: Started in 2024?
                if (biz.startedIn2024) safeCheck(scForm, 'topmostSubform[0].Page1[0].c1_3[0]');

                // Line I: Did you make payments requiring 1099?
                if (biz.requiredToFile1099) {
                    safeCheck(scForm, 'topmostSubform[0].Page1[0].c1_4[0]'); // Yes (Line I)
                    
                    // Line J: Did you file them?
                    if (biz.didFile1099) {
                        safeCheck(scForm, 'topmostSubform[0].Page1[0].c1_5[0]'); // Yes (Line J)
                    } else {
                        safeCheck(scForm, 'topmostSubform[0].Page1[0].c1_5[1]'); // No (Line J)
                    }
                } else {
                    safeCheck(scForm, 'topmostSubform[0].Page1[0].c1_4[1]'); // No (Line I)
                    // Line J is usually left blank if Line I is No
                }

                // --- PART I: INCOME ---
                const grossReceipts = parseFloat(biz.grossReceipts) || 0;
                const returns = parseFloat(biz.returnsAndAllowances) || 0;
                const otherIncome = parseFloat(biz.otherIncome) || 0;

                // Part III Calculation (COGS) needed for Line 4
                const inventoryStart = parseFloat(biz.inventoryBeginning) || 0;
                const purchases = parseFloat(biz.purchases) || 0;
                const labor = parseFloat(biz.costOfLabor) || 0;
                const materials = parseFloat(biz.materialsAndSupplies) || 0;
                const otherCosts = parseFloat(biz.otherCosts) || 0; // Ensure this exists in mockData if used
                const inventoryEnd = parseFloat(biz.inventoryEnd) || 0;
                const line40 = inventoryStart + purchases + labor + materials + otherCosts;
                const cogs = line40 - inventoryEnd;

                const grossProfit = (grossReceipts - returns) - cogs;
                const grossIncome = grossProfit + otherIncome;

                if (biz.statutoryEmployee) {
                    safeCheck(scForm, 'topmostSubform[0].Page1[0].c1_6[0]'); 
                }

                safeFill(scForm, 'topmostSubform[0].Page1[0].f1_10[0]', formatMoney(grossReceipts)); // Line 1
                safeFill(scForm, 'topmostSubform[0].Page1[0].f1_11[0]', formatMoney(returns)); // Line 2
                safeFill(scForm, 'topmostSubform[0].Page1[0].f1_12[0]', formatMoney(grossReceipts - returns)); // Line 3
                safeFill(scForm, 'topmostSubform[0].Page1[0].f1_13[0]', formatMoney(cogs)); // Line 4
                safeFill(scForm, 'topmostSubform[0].Page1[0].f1_14[0]', formatMoney(grossProfit)); // Line 5
                safeFill(scForm, 'topmostSubform[0].Page1[0].f1_15[0]', formatMoney(otherIncome)); // Line 6
                safeFill(scForm, 'topmostSubform[0].Page1[0].f1_16[0]', formatMoney(grossIncome)); // Line 7

                // --- PART II: EXPENSES ---
                safeFill(scForm, 'topmostSubform[0].Page1[0].Lines8-17[0].f1_17[0]', formatMoney(biz.advertising)); // Line 8
                safeFill(scForm, 'topmostSubform[0].Page1[0].Lines8-17[0].f1_18[0]', formatMoney(biz.carAndTruckExpenses)); // Line 9
                safeFill(scForm, 'topmostSubform[0].Page1[0].Lines8-17[0].f1_19[0]', formatMoney(biz.commissions)); // Line 10
                safeFill(scForm, 'topmostSubform[0].Page1[0].Lines8-17[0].f1_20[0]', formatMoney(biz.contractLabor)); // Line 11
                safeFill(scForm, 'topmostSubform[0].Page1[0].Lines8-17[0].f1_21[0]', formatMoney(biz.depletion)); // Line 12
                safeFill(scForm, 'topmostSubform[0].Page1[0].Lines8-17[0].f1_22[0]', formatMoney(biz.depreciation)); // Line 13
                safeFill(scForm, 'topmostSubform[0].Page1[0].Lines8-17[0].f1_23[0]', formatMoney(biz.employeeBenefitPrograms)); // Line 14
                safeFill(scForm, 'topmostSubform[0].Page1[0].Lines8-17[0].f1_24[0]', formatMoney(biz.insurance)); // Line 15
                safeFill(scForm, 'topmostSubform[0].Page1[0].Lines8-17[0].f1_25[0]', formatMoney(biz.mortgageInterest)); // Line 16a
                safeFill(scForm, 'topmostSubform[0].Page1[0].Lines8-17[0].f1_26[0]', formatMoney(biz.otherInterest)); // Line 16b
                safeFill(scForm, 'topmostSubform[0].Page1[0].Lines8-17[0].f1_27[0]', formatMoney(biz.legalAndProfessional)); // Line 17
                safeFill(scForm, 'topmostSubform[0].Page1[0].Lines18-27[0].f1_28[0]', formatMoney(biz.officeExpense)); // Line 18
                safeFill(scForm, 'topmostSubform[0].Page1[0].Lines18-27[0].f1_29[0]', formatMoney(biz.pensionAndProfitSharing)); // Line 19
                safeFill(scForm, 'topmostSubform[0].Page1[0].Lines18-27[0].f1_30[0]', formatMoney(biz.rentVehiclesMachinery)); 
                safeFill(scForm, 'topmostSubform[0].Page1[0].Lines18-27[0].f1_31[0]', formatMoney(biz.rentOtherProperty)); 
                safeFill(scForm, 'topmostSubform[0].Page1[0].Lines18-27[0].f1_32[0]', formatMoney(biz.repairsAndMaintenance)); // Line 21
                safeFill(scForm, 'topmostSubform[0].Page1[0].Lines18-27[0].f1_33[0]', formatMoney(biz.supplies)); // Line 22
                safeFill(scForm, 'topmostSubform[0].Page1[0].Lines18-27[0].f1_34[0]', formatMoney(biz.taxesAndLicenses)); // Line 23
                safeFill(scForm, 'topmostSubform[0].Page1[0].Lines18-27[0].f1_35[0]', formatMoney(biz.travel)); // Line 24a
                
                // Meals (50% deductible)
                const meals = parseFloat(biz.meals) || 0;
                const deductibleMeals = meals * 0.50;
                safeFill(scForm, 'topmostSubform[0].Page1[0].Lines18-27[0].f1_36[0]', formatMoney(deductibleMeals)); // Line 24b

                safeFill(scForm, 'topmostSubform[0].Page1[0].Lines18-27[0].f1_37[0]', formatMoney(biz.utilities)); // Line 25
                safeFill(scForm, 'topmostSubform[0].Page1[0].Lines18-27[0].f1_38[0]', formatMoney(biz.wages)); // Line 26

                // Other Expenses (Part V Total)
                const otherExpensesTotal = (biz.otherExpenses || []).reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
                safeFill(scForm, 'topmostSubform[0].Page1[0].Lines18-27[0].f1_39[0]', formatMoney(otherExpensesTotal)); // Line 27a
                const energyDed = parseFloat(biz.energyEfficientCommercialBuildingsDeduction) || 0;
                safeFill(scForm, 'topmostSubform[0].Page1[0].Lines18-27[0].f1_40[0]', formatMoney(energyDed)); // Line 27b

                // Total Expenses (Line 28)
                const totalExpenses = 
                    (parseFloat(biz.advertising) || 0) +
                    (parseFloat(biz.carAndTruckExpenses) || 0) +
                    (parseFloat(biz.commissions) || 0) +
                    (parseFloat(biz.contractLabor) || 0) +
                    (parseFloat(biz.depletion) || 0) +
                    (parseFloat(biz.depreciation) || 0) +
                    (parseFloat(biz.employeeBenefitPrograms) || 0) +
                    (parseFloat(biz.insurance) || 0) +
                    (parseFloat(biz.mortgageInterest) || 0) +
                    (parseFloat(biz.otherInterest) || 0) +
                    (parseFloat(biz.legalAndProfessional) || 0) +
                    (parseFloat(biz.officeExpense) || 0) +
                    (parseFloat(biz.pensionAndProfitSharing) || 0) +
                    (parseFloat(biz.rentVehicles) || 0) +
                    (parseFloat(biz.rentMachinery) || 0) +
                    (parseFloat(biz.repairsAndMaintenance) || 0) +
                    (parseFloat(biz.supplies) || 0) +
                    (parseFloat(biz.taxesAndLicenses) || 0) +
                    (parseFloat(biz.travel) || 0) +
                    deductibleMeals +
                    (parseFloat(biz.utilities) || 0) +
                    (parseFloat(biz.wages) || 0) +
                    otherExpensesTotal;

                safeFill(scForm, 'topmostSubform[0].Page1[0].f1_41[0]', formatMoney(totalExpenses)); // Line 28

                // Tentative Profit (Line 29)
                const tentativeProfit = grossIncome - totalExpenses;
                safeFill(scForm, 'topmostSubform[0].Page1[0].f1_42[0]', formatMoney(tentativeProfit)); // Line 29

                // --- LINE 30: HOME OFFICE (Simplified Method) ---
                const homeArea = parseFloat(biz.homeOffice?.areaUsed) || 0;
                const totalArea = parseFloat(biz.homeOffice?.totalArea) || 0;
                
                // Calculate potential deduction
                const calculatedHomeDed = Math.min(homeArea, 300) * 5; 
                
                // Apply IRS Limitation: Cannot exceed tentative profit
                const allowedHomeDed = Math.max(0, Math.min(calculatedHomeDed, tentativeProfit));

                
                    // Simplified Method Inputs (a and b)
                    safeFill(scForm, 'topmostSubform[0].Page1[0].Line30_ReadOrder[0].f1_43[0]', String(totalArea) || ''); // Total Sq Ft
                    safeFill(scForm, 'topmostSubform[0].Page1[0].Line30_ReadOrder[0].f1_44[0]', String(homeArea) || '');  // Business Sq Ft
                    
                    // Line 30 Amount
                    safeFill(scForm, 'topmostSubform[0].Page1[0].f1_45[0]', formatMoney(allowedHomeDed) || 0); 
                

                console.log("allowedHomeDed:", allowedHomeDed);

                // --- LINE 31: NET PROFIT OR (LOSS) ---
                const netProfit = tentativeProfit - allowedHomeDed;
                safeFill(scForm, 'topmostSubform[0].Page1[0].f1_46[0]', formatMoney(netProfit)); 

                // --- LINE 32: AT RISK (Only if Loss) ---
                if (netProfit < 0) {
                    // Only check these boxes if there is a loss
                    if (biz.allInvestmentAtRisk) {
                        safeCheck(scForm, 'topmostSubform[0].Page1[0].c1_7[0]'); // 32a: All at risk
                    } else {
                        safeCheck(scForm, 'topmostSubform[0].Page1[0].c1_7[1]'); // 32b: Some not at risk
                    }
                } 

                // --- PART III: COST OF GOODS SOLD ---
                if (biz.inventoryMethod === 'Cost') {
                    safeCheck(scForm, 'topmostSubform[0].Page2[0].c2_1[0]'); // 33a
                } else if (biz.inventoryMethod === 'Lower') {
                    safeCheck(scForm, 'topmostSubform[0].Page2[0].c2_2[0]'); // 33b
                } else {
                    safeCheck(scForm, 'topmostSubform[0].Page2[0].c2_3[0]'); // 33c (Other)
                }

                // Line 34: Was there any change in determining quantities, costs, or valuations?
                if (biz.inventoryChange) {
                    safeCheck(scForm, 'topmostSubform[0].Page2[0].c2_4[0]'); // Yes
                } else {
                    safeCheck(scForm, 'topmostSubform[0].Page2[0].c2_4[1]'); // No
                }
                safeFill(scForm, 'topmostSubform[0].Page2[0].f2_1[0]', formatMoney(inventoryStart)); // Line 35
                safeFill(scForm, 'topmostSubform[0].Page2[0].f2_2[0]', formatMoney(purchases)); // Line 36
                safeFill(scForm, 'topmostSubform[0].Page2[0].f2_3[0]', formatMoney(labor)); // Line 37
                safeFill(scForm, 'topmostSubform[0].Page2[0].f2_4[0]', formatMoney(materials)); // Line 38
                // Line 39: Other Costs
                safeFill(scForm, 'topmostSubform[0].Page2[0].f2_5[0]', formatMoney(otherCosts)); 

                // Line 40: Cost of goods available for sale (Sum of 35-39)
                
                safeFill(scForm, 'topmostSubform[0].Page2[0].f2_6[0]', formatMoney(line40)); 

                // Line 41: Inventory at end of year
                safeFill(scForm, 'topmostSubform[0].Page2[0].f2_7[0]', formatMoney(inventoryEnd)); 

                // Line 42: Cost of goods sold (Line 40 - Line 41)
                
                safeFill(scForm, 'topmostSubform[0].Page2[0].f2_8[0]', formatMoney(cogs));

                // --- PART IV: VEHICLE INFORMATION ---
                if (biz.vehicleInfo) {
                    // Line 43: Date placed in service (Split into Month, Day, Year)
                    if (biz.vehicleInfo.inServiceDate) {
                        const d = new Date(biz.vehicleInfo.inServiceDate);
                        const userTimezoneOffset = d.getTimezoneOffset() * 60000;
                        const adjustedDate = new Date(d.getTime() + userTimezoneOffset);
                        
                        const month = String(adjustedDate.getMonth() + 1).padStart(2, '0');
                        const day = String(adjustedDate.getDate()).padStart(2, '0');
                        const year = String(adjustedDate.getFullYear());

                        // FIX: Fill 3 separate fields
                        safeFill(scForm, 'topmostSubform[0].Page2[0].f2_09[0]', month); // Month
                        safeFill(scForm, 'topmostSubform[0].Page2[0].f2_10[0]', day);   // Day
                        safeFill(scForm, 'topmostSubform[0].Page2[0].f2_11[0]', year);  // Year
                    }

                    // FIX: Shifted Miles fields down by 2 (was 10, 11, 12 -> now 12, 13, 14)
                    safeFill(scForm, 'topmostSubform[0].Page2[0].f2_12[0]', String(biz.vehicleInfo.businessMiles || '')); // 44a
                    safeFill(scForm, 'topmostSubform[0].Page2[0].f2_13[0]', String(biz.vehicleInfo.commutingMiles || '')); // 44b
                    safeFill(scForm, 'topmostSubform[0].Page2[0].f2_14[0]', String(biz.vehicleInfo.otherMiles || '')); // 44c

                    // Line 45: Available for personal use?
                    if (biz.vehicleInfo.availableForPersonalUse) {
                        safeCheck(scForm, 'topmostSubform[0].Page2[0].c2_5[0]'); // Yes
                    } else {
                        safeCheck(scForm, 'topmostSubform[0].Page2[0].c2_5[1]'); // No
                    }

                    // Line 46: Another vehicle available?
                    if (biz.vehicleInfo.anotherVehicleAvailable) {
                        safeCheck(scForm, 'topmostSubform[0].Page2[0].c2_6[0]'); // Yes
                    } else {
                        safeCheck(scForm, 'topmostSubform[0].Page2[0].c2_6[1]'); // No
                    }

                    // Line 47a: Evidence?
                    if (biz.vehicleInfo.hasEvidence) {
                        safeCheck(scForm, 'topmostSubform[0].Page2[0].c2_7[0]'); // Yes
                    } else {
                        safeCheck(scForm, 'topmostSubform[0].Page2[0].c2_7[1]'); // No
                    }

                    // Line 47b: Written?
                    if (biz.vehicleInfo.isEvidenceWritten) {
                        safeCheck(scForm, 'topmostSubform[0].Page2[0].c2_8[0]'); // Yes
                    } else {
                        // Only check No if they have evidence but it's not written
                        // If 47a is No, 47b is usually left blank
                        if (biz.vehicleInfo.hasEvidence) {
                            safeCheck(scForm, 'topmostSubform[0].Page2[0].c2_8[1]'); // No
                        }
                    }
                }

                // --- PART V: OTHER EXPENSES ---
                (biz.otherExpenses || []).slice(0, 9).forEach((exp, idx) => {
                    const itemNum = idx + 1;
                    const descId = 15 + (idx * 2);
                    const amtId = 16 + (idx * 2);
                    
                    // Construct the nested field names
                    const descField = `topmostSubform[0].Page2[0].PartVTable[0].Item${itemNum}[0].f2_${descId}[0]`;
                    const amtField = `topmostSubform[0].Page2[0].PartVTable[0].Item${itemNum}[0].f2_${amtId}[0]`;

                    safeFill(scForm, descField, exp.description);
                    safeFill(scForm, amtField, formatMoney(exp.amount));
                });
                
                // Total Other Expenses (Line 48)
                // Note: Based on the sequence (last item ends at f2_32), the total is likely f2_33 or similar.
                // I will stick with f2_42 for now unless you have the specific ID for the total line.
                safeFill(scForm, 'topmostSubform[0].Page2[0].f2_33[0]', formatMoney(otherExpensesTotal));

                // Flatten and Merge this specific Schedule C page
                scForm.flatten();
                const scPages = await pdfDoc.copyPages(tempDoc, tempDoc.getPageIndices());
                scPages.forEach(page => pdfDoc.addPage(page));
            } // --- LOOP ENDS HERE ---
            
        } catch (e) {
            console.error("Error generating Schedule C:", e);
        }
    }
// ===========================================================================
    // SCHEDULE SE: SELF-EMPLOYMENT TAX
    // ===========================================================================
    
    // Calculate SE Income again locally or pass from totals
    // For simplicity, we will re-sum the Schedule C profits that are NOT statutory
    let totalSchedCProfit = 0;
    let totalNotary = 0;
    let totalCRP = 0;

    scheduleCData.forEach(biz => {
        if (!biz.statutoryEmployee) {
            // ... (Replicate the net profit calc logic here or pass it in `totals`) ...
            // Assuming you pass `totals.totalSEIncome` etc. is cleaner, but for now:
            // We will assume `totals.netBusinessIncome` is close enough for the PDF trigger
            // But for accurate filling, we need the specific SE math.
            
            // Let's assume you passed `totals.seData` object containing these values
        }
    });
    console.log("DEBUG 6: Total SE Income:", totals.sched2_line4);
    // Check if SE Tax exists
    if (totals.sched2_line4 > 0) { // This is where SE tax flows to
        try {
            console.log("Generating Schedule SE...");
            const seBytes = await fetch('/f1040sse.pdf').then(res => res.arrayBuffer());
            const seDoc = await PDFDocument.load(seBytes);
            const seForm = seDoc.getForm();

            // Header
            safeFill(seForm, 'topmostSubform[0].Page1[0].f1_1[0]', `${formData.firstName} ${formData.lastName}`);
            safeFill(seForm, 'topmostSubform[0].Page1[0].f1_2[0]', formData.ssn);

            // --- PART I: SELF-EMPLOYMENT TAX ---

            if (formData.scheduleSE?.ministerFiledForm4361) {
                safeCheck(seForm, 'topmostSubform[0].Page1[0].c1_1[0]'); 
            }
            
            // Line 2: Net profit from Schedule C
            // Note: We need the raw profit BEFORE the 92.35% adjustment
            // We can back-calculate or pass it. Let's assume we pass `totals.combinedSEIncome` (raw)
            // For this example, I'll use the calculated tax to reverse-engineer or just fill the tax line
            // Ideally, you pass `totals.seCalculations` object.
            
            // Let's assume we have these values from the App.js calculation:
            const rawNetProfit = totals.totalSEIncome; // You need to expose this in App.js return
            const notaryAdj = totals.totalNotaryExempt;
            const crpAdj = totals.totalCRPExempt;

            safeFill(seForm, 'topmostSubform[0].Page1[0].f1_3[0]', '0'); 

            // Line 1b: Conservation Reserve Program (CRP) payments
            // Only filled if the user entered an amount in the "Income" tab
            if (crpAdj > 0) {
                safeFill(seForm, 'topmostSubform[0].Page1[0].f1_4[0]', formatMoney(crpAdj)); 
            }
            
            safeFill(seForm, 'topmostSubform[0].Page1[0].f1_5[0]', formatMoney(rawNetProfit)); // Line 2
            
            // Line 3: Combine lines
            const line3 = rawNetProfit - notaryAdj - crpAdj; // Simplified
            safeFill(seForm, 'topmostSubform[0].Page1[0].f1_6[0]', formatMoney(line3)); // Line 3

            const line4a = line3 * 0.9235;
            safeFill(seForm, 'topmostSubform[0].Page1[0].f1_7[0]', formatMoney(line4a)); 

            // Line 4b: Optional Methods (Part II)
            let line4b = 0;
            const useOptional = formData.scheduleSE?.useNonfarmOptionalMethod;
            const totalGross = totals.totalSEGross || 0;
            const totalNet = totals.totalSEIncome || 0;

            if (useOptional) {
                // --- PART II: OPTIONAL METHODS ---
                safeFill(seForm, 'topmostSubform[0].Page1[0].f1_24[0]', 0);
                // Line 16: Net nonfarm profits
                safeFill(seForm, 'topmostSubform[0].Page2[0].f2_3[0]', formatMoney(totalNet)); 

                // Line 17: Calculation
                // If Gross <= 10,860 -> 2/3 Gross
                // If Gross > 10,860 -> 7,240
                let optionalAmount = 0;
                if (totalGross <= 10860) {
                    optionalAmount = totalGross * (2/3);
                } else {
                    optionalAmount = 7240;
                }
                
                safeFill(seForm, 'topmostSubform[0].Page2[0].f2_4[0]', formatMoney(optionalAmount)); // Line 17
                
                // Set Line 4b to this amount
                line4b = optionalAmount;
            }

            // Fill Line 4b on Page 1
            safeFill(seForm, 'topmostSubform[0].Page1[0].f1_8[0]', formatMoney(line4b)); 

            // Line 4c: Combine lines 4a and 4b
            // Note: If using optional, we usually treat 4a as 0 or the form math adds them.
            // For the PDF, we just sum them visually.
            const line4c = line4a + line4b;
            safeFill(seForm, 'topmostSubform[0].Page1[0].f1_9[0]', formatMoney(line4c)); 

            let line5a = 0;
            scheduleCData.forEach(biz => {
                line5a += (parseFloat(biz.churchEmployeeIncome) || 0);
            });
            safeFill(seForm, 'topmostSubform[0].Page1[0].Line5a_ReadOrder[0].f1_10[0]', formatMoney(line5a)); 

            // Line 5b: Multiply 5a by 92.35%
            const line5b = line5a * 0.9235;
            safeFill(seForm, 'topmostSubform[0].Page1[0].f1_11[0]', formatMoney(line5b)); 

            // --- LINE 6: Total (Line 4c + 5b) ---
            const line6 = line4c + line5b;
            safeFill(seForm, 'topmostSubform[0].Page1[0].f1_12[0]', formatMoney(line6)); 

            // Line 7: Maximum for SS
            // 2024 Limit is hardcoded in PDF usually, or we fill it
            // safeFill(seForm, '...', '168,600'); 

            // --- LINE 8: WAGES & TIPS ---
            
            // Line 8a: Total SS wages (W-2)
            const w2Wages = totals.totalW2Wages || 0;
            safeFill(seForm, 'topmostSubform[0].Page1[0].Line8a_ReadOrder[0].f1_14[0]', formatMoney(w2Wages)); 

            // Line 8b: Unreported Tips (Form 4137)
            // We grab this from the Form 8919/4137 data object we created
            const unreportedTips = parseFloat(formData.form8919?.unreportedTipsSubjectToSS) || 0;
            safeFill(seForm, 'topmostSubform[0].Page1[0].f1_15[0]', formatMoney(unreportedTips)); 

            // Line 8c: Wages from Form 8919
            const wages8919 = totals.form_8919_total_wages || 0;
            safeFill(seForm, 'topmostSubform[0].Page1[0].f1_16[0]', formatMoney(wages8919)); 

            // Line 8d: Total (8a + 8b + 8c)
            const totalSSWages = w2Wages + unreportedTips + wages8919;
            safeFill(seForm, 'topmostSubform[0].Page1[0].f1_17[0]', formatMoney(totalSSWages)); 

            const ssWageBase = 176100;

            // --- LINE 9: SUBTRACTION ---
            // Subtract Line 8d from Line 7
            const line9 = Math.max(0, ssWageBase - totalSSWages);
            safeFill(seForm, 'topmostSubform[0].Page1[0].f1_18[0]', formatMoney(line9)); 

            // --- LINE 10: SMALLER OF LINE 6 OR 9 ---
            // Note: Field IDs shift down by 2 because of the extra 8b/8c/8d lines
            const line10 = Math.min(line6, line9);
            safeFill(seForm, 'topmostSubform[0].Page1[0].f1_19[0]', formatMoney(line10)); 

            // Line 11: SS Tax (12.4%)
            const line11 = line10 * 0.124;
            safeFill(seForm, 'topmostSubform[0].Page1[0].f1_20[0]', formatMoney(line11)); 

            // Line 12: Medicare Tax (2.9%)
            const line12 = line6 * 0.029;
            safeFill(seForm, 'topmostSubform[0].Page1[0].f1_21[0]', formatMoney(line12)); 

            // Line 13: Total SE Tax
            const line13 = line11 + line12;
            safeFill(seForm, 'topmostSubform[0].Page1[0].f1_22[0]', formatMoney(line13)); 

            

            seForm.flatten();
            const sePages = await pdfDoc.copyPages(seDoc, seDoc.getPageIndices());
            sePages.forEach(page => pdfDoc.addPage(page));

        } catch (e) {
            console.error("Error generating Schedule SE:", e);
        }
    }

    // ===========================================================================
    // SCHEDULE D: CAPITAL GAINS AND LOSSES
    // ===========================================================================
    
    // 1. Standard Grid Investments (Check proceeds in the new buckets)
    // We check if any of the summary buckets have proceeds or cost > 0
    const hasInvestments = totals.hasAssetSales;

    // 2. Carryovers & Distributions
    const hasCarryover = (parseFloat(formData.capitalLossCarryoverShort) > 0 || parseFloat(formData.capitalLossCarryoverLong) > 0);
    const hasDistributions = (parseFloat(formData.capitalGainDistributions) > 0);

    // 3. Other Forms & K-1s (Lines 4, 5, 11, 12)
    const hasOtherFormGains = (
        (parseFloat(formData.schedD_line4_shortTermFormGains) || 0) !== 0 ||
        (parseFloat(formData.schedD_line5_shortTermK1Gains) || 0) !== 0 ||
        (parseFloat(formData.schedD_line11_longTermFormGains) || 0) !== 0 ||
        (parseFloat(formData.schedD_line12_longTermK1Gains) || 0) !== 0
    );

    // 4. Special Gains (Lines 18, 19)
    const hasSpecialGains = (
        (parseFloat(formData.collectiblesGain) || 0) > 0 ||
        (parseFloat(formData.unrecapturedSection1250Gain) || 0) > 0
    );

    // 5. Manual Override
    const hasManualOverride = (parseFloat(formData.manualCapitalGains) || 0) !== 0;

    console.log("DEBUG 7: Schedule D Triggers - Investments:", hasInvestments, "Carryover:", hasCarryover, "Distributions:", hasDistributions, "Other Forms:", hasOtherFormGains, "Special Gains:", hasSpecialGains);

    
    // --- FINAL TRIGGER ---
    if (hasInvestments || hasCarryover || hasDistributions || hasOtherFormGains || hasSpecialGains || hasManualOverride) {
        try {
            console.log("Generating Schedule D...");
            const sdBytes = await fetch('/f1040sd.pdf').then(res => res.arrayBuffer());
            const sdDoc = await PDFDocument.load(sdBytes);
            const sdForm = sdDoc.getForm();

            // Header
            safeFill(sdForm, 'topmostSubform[0].Page1[0].f1_1[0]', `${formData.firstName} ${formData.lastName}`);
            safeFill(sdForm, 'topmostSubform[0].Page1[0].f1_2[0]', formData.ssn);

            // --- PART I: SHORT-TERM ---
            
            // QOF Questions
            if (formData.disposedOfQOF) safeCheck(sdForm, 'topmostSubform[0].Page1[0].c1_1[0]'); // Yes
            else safeCheck(sdForm, 'topmostSubform[0].Page1[0].c1_1[1]'); // No

            if (formData.acquiredQOF) safeCheck(sdForm, 'topmostSubform[0].Page1[0].c1_2[0]'); // Yes (Note: ID might be c1_2 based on standard forms)
            else safeCheck(sdForm, 'topmostSubform[0].Page1[0].c1_2[1]'); // No

            // --- Line 1a vs 1b Logic ---
            const boxA = totals.st_A || { proceeds: 0, cost: 0, adjustments: 0, gain: 0 };
            const boxA_clean = totals.st_A_clean || { proceeds: 0, cost: 0, adjustments: 0, gain: 0 };
            const boxA_adj = totals.st_A_adj || { proceeds: 0, cost: 0, adjustments: 0, gain: 0 };
            
            const isQOF = formData.disposedOfQOF || formData.acquiredQOF;

            if (isQOF) {
                // If QOF involved, force everything to Line 1b (Form 8949 required)
                // We combine clean + adj into Line 1b
                const combinedProceeds = boxA.proceeds;
                const combinedCost = boxA.cost;
                const combinedAdj = boxA.adjustments;
                const combinedGain = boxA.gain;

                safeFill(sdForm, 'topmostSubform[0].Page1[0].Table_PartI[0].Row1b[0].f1_7[0]', formatMoney(combinedProceeds)); 
                safeFill(sdForm, 'topmostSubform[0].Page1[0].Table_PartI[0].Row1b[0].f1_8[0]', formatMoney(combinedCost));     
                safeFill(sdForm, 'topmostSubform[0].Page1[0].Table_PartI[0].Row1b[0].f1_9[0]', formatMoney(combinedAdj)); 
                safeFill(sdForm, 'topmostSubform[0].Page1[0].Table_PartI[0].Row1b[0].f1_10[0]', formatMoney(combinedGain));     
                
                // Zero out 1a
                safeFill(sdForm, 'topmostSubform[0].Page1[0].Table_PartI[0].Row1a[0].f1_3[0]', '0.00'); 
                safeFill(sdForm, 'topmostSubform[0].Page1[0].Table_PartI[0].Row1a[0].f1_4[0]', '0.00');     
                safeFill(sdForm, 'topmostSubform[0].Page1[0].Table_PartI[0].Row1a[0].f1_6[0]', '0.00');     
            } else {
                // STANDARD LOGIC: Split based on adjustments
                
                // Line 1a: Clean transactions (No Adjustments)
                safeFill(sdForm, 'topmostSubform[0].Page1[0].Table_PartI[0].Row1a[0].f1_3[0]', formatMoney(boxA_clean.proceeds)); 
                safeFill(sdForm, 'topmostSubform[0].Page1[0].Table_PartI[0].Row1a[0].f1_4[0]', formatMoney(boxA_clean.cost));     
                safeFill(sdForm, 'topmostSubform[0].Page1[0].Table_PartI[0].Row1a[0].f1_6[0]', formatMoney(boxA_clean.gain));     

                // Line 1b: Adjusted transactions (Has Adjustments)
                safeFill(sdForm, 'topmostSubform[0].Page1[0].Table_PartI[0].Row1b[0].f1_7[0]', formatMoney(boxA_adj.proceeds));   
                safeFill(sdForm, 'topmostSubform[0].Page1[0].Table_PartI[0].Row1b[0].f1_8[0]', formatMoney(boxA_adj.cost));       
                safeFill(sdForm, 'topmostSubform[0].Page1[0].Table_PartI[0].Row1b[0].f1_9[0]', formatMoney(boxA_adj.adjustments)); 
                safeFill(sdForm, 'topmostSubform[0].Page1[0].Table_PartI[0].Row1b[0].f1_10[0]', formatMoney(boxA_adj.gain));       
            }

            // Line 2 (Box B)
            const boxB = totals.st_B || { proceeds: 0, cost: 0, adjustments: 0, gain: 0 };
            safeFill(sdForm, 'topmostSubform[0].Page1[0].Table_PartI[0].Row2[0].f1_11[0]', formatMoney(boxB.proceeds)); 
            safeFill(sdForm, 'topmostSubform[0].Page1[0].Table_PartI[0].Row2[0].f1_12[0]', formatMoney(boxB.cost));     
            safeFill(sdForm, 'topmostSubform[0].Page1[0].Table_PartI[0].Row2[0].f1_13[0]', formatMoney(boxB.adjustments)); 
            safeFill(sdForm, 'topmostSubform[0].Page1[0].Table_PartI[0].Row2[0].f1_14[0]', formatMoney(boxB.gain));     

            // Line 3 (Box C)
            const boxC = totals.st_C || { proceeds: 0, cost: 0, adjustments: 0, gain: 0 };
            safeFill(sdForm, 'topmostSubform[0].Page1[0].Table_PartI[0].Row3[0].f1_15[0]', formatMoney(boxC.proceeds)); 
            safeFill(sdForm, 'topmostSubform[0].Page1[0].Table_PartI[0].Row3[0].f1_16[0]', formatMoney(boxC.cost));     
            safeFill(sdForm, 'topmostSubform[0].Page1[0].Table_PartI[0].Row3[0].f1_17[0]', formatMoney(boxC.adjustments)); 
            safeFill(sdForm, 'topmostSubform[0].Page1[0].Table_PartI[0].Row3[0].f1_18[0]', formatMoney(boxC.gain));    

            // Line 4: Short-Term Gain from Forms
            safeFill(sdForm, 'topmostSubform[0].Page1[0].f1_19[0]', formatMoney(formData.schedD_line4_shortTermFormGains));

            // Line 5: Short-Term Gain from K-1
            safeFill(sdForm, 'topmostSubform[0].Page1[0].f1_20[0]', formatMoney(formData.schedD_line5_shortTermK1Gains));
            
            // Line 6: Short-Term Carryover (Enter as negative)
            if (formData.capitalLossCarryoverShort > 0) {
                const lossVal = formData.capitalLossCarryoverShort * -1;
                safeFill(sdForm, 'topmostSubform[0].Page1[0].f1_21[0]', formatMoney(lossVal)); 
            }

            // Line 7: Net Short-Term
            safeFill(sdForm, 'topmostSubform[0].Page1[0].f1_22[0]', formatMoney(totals.schedD_Line7)); 

            // --- PART II: LONG-TERM ---
            
            const boxD = totals.lt_D || { proceeds: 0, cost: 0, adjustments: 0, gain: 0 };
            const boxD_clean = totals.lt_D_clean || { proceeds: 0, cost: 0, adjustments: 0, gain: 0 };
            const boxD_adj = totals.lt_D_adj || { proceeds: 0, cost: 0, adjustments: 0, gain: 0 };

            if (isQOF) {
                // Force Line 8b
                const combinedProceeds = boxD.proceeds;
                const combinedCost = boxD.cost;
                const combinedAdj = boxD.adjustments;
                const combinedGain = boxD.gain;

                safeFill(sdForm, 'topmostSubform[0].Page1[0].Table_PartII[0].Row8b[0].f1_27[0]', formatMoney(combinedProceeds)); 
                safeFill(sdForm, 'topmostSubform[0].Page1[0].Table_PartII[0].Row8b[0].f1_28[0]', formatMoney(combinedCost));     
                safeFill(sdForm, 'topmostSubform[0].Page1[0].Table_PartII[0].Row8b[0].f1_29[0]', formatMoney(combinedAdj)); 
                safeFill(sdForm, 'topmostSubform[0].Page1[0].Table_PartII[0].Row8b[0].f1_30[0]', formatMoney(combinedGain));     
                
                // Zero out 8a
                safeFill(sdForm, 'topmostSubform[0].Page1[0].Table_PartII[0].Row8a[0].f1_23[0]', '0.00'); 
                safeFill(sdForm, 'topmostSubform[0].Page1[0].Table_PartII[0].Row8a[0].f1_24[0]', '0.00');     
                safeFill(sdForm, 'topmostSubform[0].Page1[0].Table_PartII[0].Row8a[0].f1_26[0]', '0.00');
            } else {
                // Line 8a: Clean
                safeFill(sdForm, 'topmostSubform[0].Page1[0].Table_PartII[0].Row8a[0].f1_23[0]', formatMoney(boxD_clean.proceeds)); 
                safeFill(sdForm, 'topmostSubform[0].Page1[0].Table_PartII[0].Row8a[0].f1_24[0]', formatMoney(boxD_clean.cost));     
                safeFill(sdForm, 'topmostSubform[0].Page1[0].Table_PartII[0].Row8a[0].f1_26[0]', formatMoney(boxD_clean.gain));     

                // Line 8b: Adjusted
                safeFill(sdForm, 'topmostSubform[0].Page1[0].Table_PartII[0].Row8b[0].f1_27[0]', formatMoney(boxD_adj.proceeds)); 
                safeFill(sdForm, 'topmostSubform[0].Page1[0].Table_PartII[0].Row8b[0].f1_28[0]', formatMoney(boxD_adj.cost));     
                safeFill(sdForm, 'topmostSubform[0].Page1[0].Table_PartII[0].Row8b[0].f1_29[0]', formatMoney(boxD_adj.adjustments)); 
                safeFill(sdForm, 'topmostSubform[0].Page1[0].Table_PartII[0].Row8b[0].f1_30[0]', formatMoney(boxD_adj.gain));     
            }

            // Line 9 (Box E)
            const boxE = totals.lt_E || { proceeds: 0, cost: 0, adjustments: 0, gain: 0 };
            safeFill(sdForm, 'topmostSubform[0].Page1[0].Table_PartII[0].Row9[0].f1_31[0]', formatMoney(boxE.proceeds)); 
            safeFill(sdForm, 'topmostSubform[0].Page1[0].Table_PartII[0].Row9[0].f1_32[0]', formatMoney(boxE.cost));     
            safeFill(sdForm, 'topmostSubform[0].Page1[0].Table_PartII[0].Row9[0].f1_33[0]', formatMoney(boxE.adjustments)); 
            safeFill(sdForm, 'topmostSubform[0].Page1[0].Table_PartII[0].Row9[0].f1_34[0]', formatMoney(boxE.gain));     

            // Line 10 (Box F)
            const boxF = totals.lt_F || { proceeds: 0, cost: 0, adjustments: 0, gain: 0 };
            safeFill(sdForm, 'topmostSubform[0].Page1[0].Table_PartII[0].Row10[0].f1_35[0]', formatMoney(boxF.proceeds)); 
            safeFill(sdForm, 'topmostSubform[0].Page1[0].Table_PartII[0].Row10[0].f1_36[0]', formatMoney(boxF.cost));     
            safeFill(sdForm, 'topmostSubform[0].Page1[0].Table_PartII[0].Row10[0].f1_37[0]', formatMoney(boxF.adjustments)); 
            safeFill(sdForm, 'topmostSubform[0].Page1[0].Table_PartII[0].Row10[0].f1_38[0]', formatMoney(boxF.gain));     

            // Line 11: Long-Term Gain from Forms
            safeFill(sdForm, 'topmostSubform[0].Page1[0].f1_39[0]', formatMoney(formData.schedD_line11_longTermFormGains));

            // Line 12: Long-Term Gain from K-1
            safeFill(sdForm, 'topmostSubform[0].Page1[0].f1_40[0]', formatMoney(formData.schedD_line12_longTermK1Gains));

            // Line 13: Capital Gain Distributions
            safeFill(sdForm, 'topmostSubform[0].Page1[0].f1_41[0]', formatMoney(formData.capitalGainDistributions));

            // Line 14: Long-Term Carryover
            if (formData.capitalLossCarryoverLong > 0) {
                const lossVal = formData.capitalLossCarryoverLong * -1;
                safeFill(sdForm, 'topmostSubform[0].Page1[0].f1_42[0]', formatMoney(lossVal));
            }

            // Line 15: Net Long-Term
            safeFill(sdForm, 'topmostSubform[0].Page1[0].f1_43[0]', formatMoney(totals.schedD_Line15));

            // --- PART III: SUMMARY ---
            
             // Line 16: Combine Lines 7 and 15
            const netGainLoss = totals.schedD_Line16;
            safeFill(sdForm, 'topmostSubform[0].Page2[0].f2_1[0]', formatMoney(netGainLoss));

            // Logic Split: Gain vs Loss
            if (netGainLoss >= 0) {
                // --- GAIN SCENARIO ---
                
                // Line 17: Are both 15 and 16 gains?
                const bothGains = totals.schedD_Line15 > 0 && netGainLoss > 0;
                const collectibles = parseFloat(formData.collectiblesGain) || 0;
                const unrecaptured1250 = parseFloat(formData.unrecapturedSection1250Gain) || 0;
                if (bothGains) {
                    safeCheck(sdForm, 'topmostSubform[0].Page2[0].c2_1[0]'); // Yes
                    
                    // Lines 18-20 are ONLY filled if Line 17 is Yes
                    
                    // Line 18: 28% Rate Gain
                    if (collectibles > 0) {
                        safeFill(sdForm, 'topmostSubform[0].Page2[0].f2_2[0]', formatMoney(collectibles));
                    }

                    // Line 19: Unrecaptured Section 1250 Gain
                    if (unrecaptured1250 > 0) {
                        safeFill(sdForm, 'topmostSubform[0].Page2[0].f2_3[0]', formatMoney(unrecaptured1250));
                    }

                    // Line 20: Are Lines 18 and 19 both zero?
                    if (collectibles === 0 && unrecaptured1250 === 0) {
                        safeCheck(sdForm, 'topmostSubform[0].Page2[0].c2_2[0]'); // Yes
                    } else {
                        safeCheck(sdForm, 'topmostSubform[0].Page2[0].c2_2[1]'); // No
                    }
                } else {
                    safeCheck(sdForm, 'topmostSubform[0].Page2[0].c2_1[1]'); // No (Skip 18-20)
                }

                // Line 21 is skipped for Gains

                // --- LINE 22: QUALIFIED DIVIDENDS ---
                // Only answered if Line 16 is a Gain
                const qualDivs = parseFloat(formData.qualifiedDividends) || 0;
                if (qualDivs > 0) {
                    if (collectibles === 0 && unrecaptured1250 === 0) {
                        // Do nothing, logic handled elsewhere or user fills manually
                    }
                    safeCheck(sdForm, 'topmostSubform[0].Page2[0].c2_3[0]'); // Yes
                } else {
                    safeCheck(sdForm, 'topmostSubform[0].Page2[0].c2_3[1]'); // No
                }

            } else {
                // --- LOSS SCENARIO ---
                
                // Line 17 is skipped (or No)
                // Lines 18-20 are skipped
                
                // Line 21: Allowable Loss
                // Enter as negative number (in parentheses)
                const lossClaimed = totals.allowableLoss * -1;
                safeFill(sdForm, 'topmostSubform[0].Page2[0].f2_26[0]', formatMoney(lossClaimed));
                
                // Line 22 is skipped for Losses
            }

            sdForm.flatten();
            const sdPages = await pdfDoc.copyPages(sdDoc, sdDoc.getPageIndices());
            sdPages.forEach(page => pdfDoc.addPage(page));

        } catch (e) {
            console.error("Error generating Schedule D:", e);
        }
    }

    let isForm8949Required = false;
    if (formData.disposedOfQOF || formData.acquiredQOF) {
        isForm8949Required = true;
    }

    const f8949Data = totals.form8949Data;
    const has8949Data = f8949Data && Object.values(f8949Data).some(arr => arr && arr.length > 0);

    if (has8949Data || isForm8949Required) {
        try {
            console.log("Generating Form 8949...");
            const f8949Bytes = await fetch('/f8949.pdf').then(res => res.arrayBuffer());
            
            // 12-BUCKET MAPPING for 2024/2025 Form 8949
            // Part I (Short-Term): Page 1
            // Part II (Long-Term): Page 2
            const buckets = [
                // --- PART I: Short-Term (Page 1) ---
                // Standard 1099-B
                { id: 'A', data: f8949Data.A, part: 1, checkbox: 'topmostSubform[0].Page1[0].c1_1[0]' }, // Index 0
                { id: 'B', data: f8949Data.B, part: 1, checkbox: 'topmostSubform[0].Page1[0].c1_1[1]' }, // Index 1
                { id: 'C', data: f8949Data.C, part: 1, checkbox: 'topmostSubform[0].Page1[0].c1_1[2]' }, // Index 2
                // Digital Assets (1099-DA)
                { id: 'G', data: f8949Data.G, part: 1, checkbox: 'topmostSubform[0].Page1[0].c1_1[3]' }, // Index 3
                { id: 'H', data: f8949Data.H, part: 1, checkbox: 'topmostSubform[0].Page1[0].c1_1[4]' }, // Index 4
                { id: 'I', data: f8949Data.I, part: 1, checkbox: 'topmostSubform[0].Page1[0].c1_1[5]' }, // Index 5

                // --- PART II: Long-Term (Page 2) ---
                // Standard 1099-B
                { id: 'D', data: f8949Data.D, part: 2, checkbox: 'topmostSubform[0].Page2[0].c2_1[0]' }, // Index 0
                { id: 'E', data: f8949Data.E, part: 2, checkbox: 'topmostSubform[0].Page2[0].c2_1[1]' }, // Index 1
                { id: 'F', data: f8949Data.F, part: 2, checkbox: 'topmostSubform[0].Page2[0].c2_1[2]' }, // Index 2
                // Digital Assets (1099-DA)
                { id: 'J', data: f8949Data.J, part: 2, checkbox: 'topmostSubform[0].Page2[0].c2_1[3]' }, // Index 3
                { id: 'K', data: f8949Data.K, part: 2, checkbox: 'topmostSubform[0].Page2[0].c2_1[4]' }, // Index 4
                { id: 'L', data: f8949Data.L, part: 2, checkbox: 'topmostSubform[0].Page2[0].c2_1[5]' }  // Index 5
            ];

            // We create a new PDF document to hold all the 8949 pages
            const finalPdfDoc = await PDFDocument.create();

            for (const bucket of buckets) {
                // Skip empty buckets
                if (!bucket.data || bucket.data.length === 0) continue;

                // Configuration for this specific PDF template (11 rows per page)
                const ROWS_PER_PAGE = 11; 
                const totalPages = Math.ceil(bucket.data.length / ROWS_PER_PAGE);

                for (let i = 0; i < totalPages; i++) {
                    // Load a fresh copy of the blank form for each page we need
                    const tempDoc = await PDFDocument.load(f8949Bytes);
                    const tempForm = tempDoc.getForm();
                    
                    // Determine Page, Field, and Table prefixes based on Part (1 or 2)
                    const pagePrefix = bucket.part === 1 ? 'Page1' : 'Page2';
                    const fieldPrefix = bucket.part === 1 ? 'f1' : 'f2';
                    const tablePrefix = bucket.part === 1 ? 'Table_Line1_Part1' : 'Table_Line1_Part2';
                    
                    // 1. Fill Header Info (Name & SSN)
                    // Note: Header fields are usually f1_01/02 on Page 1 and f2_01/02 on Page 2
                    safeFill(tempForm, `topmostSubform[0].${pagePrefix}[0].${fieldPrefix}_01[0]`, `${formData.firstName} ${formData.lastName}`);
                    safeFill(tempForm, `topmostSubform[0].${pagePrefix}[0].${fieldPrefix}_02[0]`, formData.ssn);
                    
                    // 2. Check the appropriate box
                    safeCheck(tempForm, bucket.checkbox);

                    // 3. Process Rows
                    const startIdx = i * ROWS_PER_PAGE;
                    const pageItems = bucket.data.slice(startIdx, startIdx + ROWS_PER_PAGE);
                    
                    let pageProceeds = 0;
                    let pageCost = 0;
                    let pageAdj = 0;
                    let pageGain = 0;

                    pageItems.forEach((item, rowIndex) => {
                        const rowNum = rowIndex + 1; // 1-based index for XFA path
                        
                        // Calculate Base Field ID: Row 1 starts at 03. Step is 8.
                        // Formula: 3 + (rowIndex * 8)
                        const baseId = 3 + (rowIndex * 8);

                        // Helper to construct the deep hierarchical path
                        const getPath = (colOffset) => {
                            const id = String(baseId + colOffset).padStart(2, '0');
                            return `topmostSubform[0].${pagePrefix}[0].${tablePrefix}[0].Row${rowNum}[0].${fieldPrefix}_${id}[0]`;
                        };

                        // (a) Description
                        safeFill(tempForm, getPath(0), item.description);
                        // (b) Date Acquired
                        safeFill(tempForm, getPath(1), item.dateAcquired);
                        // (c) Date Sold
                        safeFill(tempForm, getPath(2), item.dateSold);
                        // (d) Proceeds
                        safeFill(tempForm, getPath(3), formatMoney(item.proceeds));
                        // (e) Cost Basis
                        safeFill(tempForm, getPath(4), formatMoney(item.costBasis));
                        // (f) Code
                        safeFill(tempForm, getPath(5), item.code);
                        // (g) Adjustment
                        safeFill(tempForm, getPath(6), item.adjustmentAmount !== 0 ? formatMoney(item.adjustmentAmount) : '');
                        // (h) Gain/Loss
                        safeFill(tempForm, getPath(7), formatMoney(item.gainLoss));

                        // Accumulate Page Totals
                        pageProceeds += item.proceeds;
                        pageCost += item.costBasis;
                        pageAdj += item.adjustmentAmount;
                        pageGain += item.gainLoss;
                    });

                    // 4. Fill Page Totals (Line 2)
                    // These sit outside the Table structure.
                    // IDs: 91 (Proceeds), 92 (Cost), 94 (Adj), 95 (Gain)
                    
                    // (d) Total Proceeds
                    safeFill(tempForm, `topmostSubform[0].${pagePrefix}[0].${fieldPrefix}_91[0]`, formatMoney(pageProceeds));
                    // (e) Total Cost
                    safeFill(tempForm, `topmostSubform[0].${pagePrefix}[0].${fieldPrefix}_92[0]`, formatMoney(pageCost));
                    // (g) Total Adjustment (Note: 93 is skipped)
                    safeFill(tempForm, `topmostSubform[0].${pagePrefix}[0].${fieldPrefix}_94[0]`, formatMoney(pageAdj));
                    // (h) Total Gain/Loss
                    safeFill(tempForm, `topmostSubform[0].${pagePrefix}[0].${fieldPrefix}_95[0]`, formatMoney(pageGain));

                    // 5. Flatten and Merge
                    tempForm.flatten();
                    
                    const docPageCount = tempDoc.getPageCount();
                    let pageIndexToCopy = 0;
                    
                    // If the PDF has 2 pages (Part 1 and Part 2), ensure we copy the correct one
                    if (docPageCount > 1) {
                        pageIndexToCopy = bucket.part - 1; 
                    }

                    const [copiedPage] = await finalPdfDoc.copyPages(tempDoc, [pageIndexToCopy]);
                    finalPdfDoc.addPage(copiedPage);
                }
            }
            
            // Save the generated 8949
            const f8949Pages = await pdfDoc.copyPages(finalPdfDoc, finalPdfDoc.getPageIndices());
f8949Pages.forEach(page => pdfDoc.addPage(page));
        } catch (e) {
            console.error("Error generating Form 8949:", e);
            alert("Error generating Form 8949. Please check the console.");
        }
    }

    // ===========================================================================
// SCHEDULE E: SUPPLEMENTAL INCOME AND LOSS
// ===========================================================================

// ===========================================================================
    // SCHEDULE E: SUPPLEMENTAL INCOME AND LOSS
    // ===========================================================================

    const schedE = formData.scheduleEData || { properties: [], partnerships: [] };
    const hasRentals = schedE.properties && schedE.properties.length > 0;
    const hasPartnerships = schedE.partnerships && schedE.partnerships.length > 0;

    if (hasRentals || hasPartnerships) {
        try {
            console.log("Generating Schedule E...");
            const seBytes = await fetch('/f1040se.pdf').then(res => res.arrayBuffer());
            const seDoc = await PDFDocument.load(seBytes);
            const seForm = seDoc.getForm();

            // --- HEADER ---
            const fullName = `${formData.firstName || ''} ${formData.lastName || ''}`.trim();
            safeFill(seForm, 'topmostSubform[0].Page1[0].f1_1[0]', fullName);
            safeFill(seForm, 'topmostSubform[0].Page1[0].f1_2[0]', formData.ssn || '');

            // --- QUESTIONS A & B ---
            if (schedE.paymentsRequiring1099) safeCheck(seForm, 'topmostSubform[0].Page1[0].c1_1[0]'); // Yes
            else safeCheck(seForm, 'topmostSubform[0].Page1[0].c1_1[1]'); // No

            if (schedE.didFile1099) safeCheck(seForm, 'topmostSubform[0].Page1[0].c1_2[0]'); // Yes
            else safeCheck(seForm, 'topmostSubform[0].Page1[0].c1_2[1]'); // No

            // --- PART I: RENTAL REAL ESTATE ---
            const props = schedE.properties.slice(0, 3);
            const rowNames = ['RowA', 'RowB', 'RowC'];
            
            let sumRents = 0;       // 23a
            let sumRoyalties = 0;   // 23b
            let sumMortgage = 0;    // 23c
            let sumTotalExp = 0;    // 23d
            let sumDepreciation = 0;// 23e
            let sumLine24 = 0;      // Total Positive Income
            let sumLine25 = 0;      // Total Losses

            props.forEach((prop, index) => {
                const rowName = rowNames[index];

                // 1a. Address
                // Your path: Table_Line1a -> RowA -> f1_3
                const addrId = (3 + index).toString();
                const addrField = `topmostSubform[0].Page1[0].Table_Line1a[0].${rowName}[0].f1_${addrId}[0]`; 
                let fullAddress = `${prop.address}, ${prop.city}, ${prop.state} ${prop.zip}`;
                
                if (prop.propertyType === '8' && prop.otherPropertyDescription) {
                    fullAddress += ` - ${prop.otherPropertyDescription}`;
                }
                safeFill(seForm, addrField, fullAddress);

                // 1b. Property Type
                // Your path: Table_Line1b -> RowA -> f1_6
                const typeId = (6 + index).toString();
                const typeField = `topmostSubform[0].Page1[0].Table_Line1b[0].${rowName}[0].f1_${typeId}[0]`;
                safeFill(seForm, typeField, prop.propertyType);

                // 2. Days
                // Your path: Table_Line2 -> RowA -> f1_9 (Rental), f1_10 (Personal)
                const rentalDaysId = (9 + (index * 2)).toString();
                const rentalDaysField = `topmostSubform[0].Page1[0].Table_Line2[0].${rowName}[0].f1_${rentalDaysId}[0]`;
                safeFill(seForm, rentalDaysField, prop.fairRentalDays);

                const personalDaysId = (10 + (index * 2)).toString();
                const personalDaysField = `topmostSubform[0].Page1[0].Table_Line2[0].${rowName}[0].f1_${personalDaysId}[0]`;
                safeFill(seForm, personalDaysField, prop.personalUseDays);

                // QJV Checkbox
                if (prop.qjv) {
                    const qjvId = 3 + index;
                    safeCheck(seForm, `topmostSubform[0].Page1[0].c1_${qjvId}[0]`);
                }

                // Income
                // Your path: Table_Income -> Line3 -> f1_16
                safeFill(seForm, `topmostSubform[0].Page1[0].Table_Income[0].Line3[0].f1_${(16 + index).toString()}[0]`, formatMoney(prop.rentsReceived));
                safeFill(seForm, `topmostSubform[0].Page1[0].Table_Income[0].Line4[0].f1_${(19 + index).toString()}[0]`, formatMoney(prop.royaltiesReceived));

                // Expenses
                const fillExp = (lineIndex, val) => {
                    // Standard calculation for lines 5-18
                    let baseId = 22 + (lineIndex * 3) + index;
                    
                    // Special handling for Line 19 (Other)
                    if (lineIndex === 14) { 
                        if (index === 0) baseId = 65; // Col A
                        if (index === 1) baseId = 66; // Col B
                        if (index === 2) baseId = 67; // Col C
                    }
                    // Special handling for Line 20 (Total Expenses)
                    if (lineIndex === 15) { 
                        if (index === 0) baseId = 68; 
                        if (index === 1) baseId = 69; 
                        if (index === 2) baseId = 70; 
                    }
                    // Special handling for Line 21 (Net Income)
                    if (lineIndex === 16) { 
                        if (index === 0) baseId = 71; 
                        if (index === 1) baseId = 72; 
                        if (index === 2) baseId = 73; 
                    }
                    // Special handling for Line 22 (Deductible Loss)
                    if (lineIndex === 17) { 
                        if (index === 0) baseId = 74; 
                        if (index === 1) baseId = 75; 
                        if (index === 2) baseId = 76; 
                    }

                    // Your path: Table_Expenses -> LineX -> f1_XX
                    // Note: Income lines (3,4) use Table_Income, Expenses use Table_Expenses
                    let tableName = "Table_Expenses";
                    
                    safeFill(seForm, `topmostSubform[0].Page1[0].${tableName}[0].Line${(5 + lineIndex).toString()}[0].f1_${baseId}[0]`, formatMoney(val));
                };

                fillExp(0, prop.advertising); // Line 5
                
                // Line 6: Auto (70 cents/mile)
                const autoAmt = (parseFloat(prop.autoMiles) || 0) * 0.70 + (parseFloat(prop.autoAndTravel) || 0);
                fillExp(1, autoAmt); 

                fillExp(2, prop.cleaningAndMaintenance); // Line 7
                fillExp(3, prop.commissions); // Line 8
                fillExp(4, prop.insurance); // Line 9
                fillExp(5, prop.legalAndProfessional); // Line 10
                fillExp(6, prop.managementFees); // Line 11
                fillExp(7, prop.mortgageInterest); // Line 12
                fillExp(8, prop.otherInterest); // Line 13
                fillExp(9, prop.repairs); // Line 14
                fillExp(10, prop.supplies); // Line 15
                fillExp(11, prop.taxes); // Line 16
                fillExp(12, prop.utilities); // Line 17
                fillExp(13, prop.depreciation); // Line 18
                
                // Line 19: Other
                const otherTotal = prop.otherExpenses.reduce((sum, i) => sum + (parseFloat(i.amount)||0), 0);
                fillExp(14, otherTotal);
                
                // Fill Description for Line 19 (f1_64)
                if (index === 0 && prop.otherExpenses.length > 0) {
                     const desc = prop.otherExpenses.map(e => e.description).join(', ');
                     safeFill(seForm, 'topmostSubform[0].Page1[0].Table_Expenses[0].Line19[0].f1_64[0]', desc);
                }

                // Line 20: Total Expenses
                const totalExp = 
                    (parseFloat(prop.advertising)||0) + autoAmt + (parseFloat(prop.cleaningAndMaintenance)||0) +
                    (parseFloat(prop.commissions)||0) + (parseFloat(prop.insurance)||0) + (parseFloat(prop.legalAndProfessional)||0) +
                    (parseFloat(prop.managementFees)||0) + (parseFloat(prop.mortgageInterest)||0) + (parseFloat(prop.otherInterest)||0) +
                    (parseFloat(prop.repairs)||0) + (parseFloat(prop.supplies)||0) + (parseFloat(prop.taxes)||0) +
                    (parseFloat(prop.utilities)||0) + (parseFloat(prop.depreciation)||0) + otherTotal;
                
                fillExp(15, totalExp); 

                // Line 21: Net Income/Loss
                const income = (parseFloat(prop.rentsReceived)||0) + (parseFloat(prop.royaltiesReceived)||0);
                const net = income - totalExp;
                fillExp(16, net); 

                // Line 22: Deductible Loss
                if (net < 0) {
                    const isVacation = (parseFloat(prop.personalUseDays) > 14 || parseFloat(prop.personalUseDays) > parseFloat(prop.fairRentalDays)*0.1);
                    if (isVacation) {
                        fillExp(17, 0); // Loss limited to 0
                    } else {
                        fillExp(17, Math.abs(net)); 
                    }
                }

                // Accumulate Totals
                if (prop.propertyType === '6') { 
                    // ROYALTY
                    if (net < 0) sumLine25 += Math.abs(net);
                    else sumLine24 += net;
                } else {
                    // RENTAL
                    if (net > 0) {
                        sumLine24 += net;
                    } else {
                        let deductibleAmount = 0;
                        const isVacation = (parseFloat(prop.personalUseDays) > 14 || parseFloat(prop.personalUseDays) > parseFloat(prop.fairRentalDays)*0.1);
                        if (!isVacation) {
                            deductibleAmount = Math.abs(net); 
                        }
                        sumLine25 += deductibleAmount;
                    }
                }
                sumRents += (parseFloat(prop.rentsReceived) || 0);
                sumRoyalties += (parseFloat(prop.royaltiesReceived) || 0);
                sumMortgage += (parseFloat(prop.mortgageInterest) || 0);
                sumTotalExp += totalExp; 
                sumDepreciation += (parseFloat(prop.depreciation) || 0);
            });

            // --- PART I TOTALS (Lines 23-26) ---
            safeFill(seForm, 'topmostSubform[0].Page1[0].f1_77[0]', formatMoney(sumRents));        // 23a
            safeFill(seForm, 'topmostSubform[0].Page1[0].f1_78[0]', formatMoney(sumRoyalties));    // 23b
            safeFill(seForm, 'topmostSubform[0].Page1[0].f1_79[0]', formatMoney(sumMortgage));     // 23c
            safeFill(seForm, 'topmostSubform[0].Page1[0].f1_80[0]', formatMoney(sumTotalExp));     // 23d
            safeFill(seForm, 'topmostSubform[0].Page1[0].f1_81[0]', formatMoney(sumDepreciation)); // 23e

            safeFill(seForm, 'topmostSubform[0].Page1[0].f1_82[0]', formatMoney(sumLine24));       // 24
            safeFill(seForm, 'topmostSubform[0].Page1[0].f1_83[0]', formatMoney(sumLine25));       // 25

            const line26Total = sumLine24 - sumLine25;
            safeFill(seForm, 'topmostSubform[0].Page1[0].f1_84[0]', formatMoney(line26Total));     // 26

            // ===========================================================================
            // PART II: INCOME OR LOSS FROM PARTNERSHIPS AND S CORPORATIONS
            // ===========================================================================

            // --- LINE 27: Prior Year Loss Limitations ---
            if (schedE.reportingPriorYearLossLimitations) {
                safeCheck(seForm, 'topmostSubform[0].Page2[0].c2_1[0]'); // Yes
            } else {
                safeCheck(seForm, 'topmostSubform[0].Page2[0].c2_1[1]'); // No
            }

            // --- LINE 28: Rows A, B, C, D ---
            const k1s = schedE.partnerships.slice(0, 4); 
            const partIIRows = ['RowA', 'RowB', 'RowC', 'RowD'];
            
            // Initialize Column Totals for Line 29a
            let totalPassiveLoss = 0;       // Col (g)
            let totalPassiveIncome = 0;     // Col (h)
            let totalNonPassiveLoss = 0;    // Col (i)
            let totalSection179 = 0;        // Col (j)
            let totalNonPassiveIncome = 0;  // Col (k)

            k1s.forEach((k1, index) => {
                const rowName = partIIRows[index];

                // --- LEFT TABLE (Cols A - F): Table_Line28a-f ---
                // Row A starts at f2_3. Row B starts at f2_6. (Step = 3)
                const leftBaseId = 3 + (index * 3);

                // (a) Name
                safeFill(seForm, `topmostSubform[0].Page2[0].Table_Line28a-f[0].${rowName}[0].f2_${leftBaseId}[0]`, k1.name);
                
                // (b) Code (P or S)
                safeFill(seForm, `topmostSubform[0].Page2[0].Table_Line28a-f[0].${rowName}[0].f2_${leftBaseId + 1}[0]`, k1.type);
                
                // (d) EIN
                safeFill(seForm, `topmostSubform[0].Page2[0].Table_Line28a-f[0].${rowName}[0].f2_${leftBaseId + 2}[0]`, k1.ein);

                // --- CHECKBOXES (Cols c, e, f) ---
                // Row A: c2_2, c2_3, c2_4. Row B: c2_5, c2_6, c2_7. (Step = 3)
                const checkBaseId = 2 + (index * 3);

                if (k1.isForeign) safeCheck(seForm, `topmostSubform[0].Page2[0].Table_Line28a-f[0].${rowName}[0].c2_${checkBaseId}[0]`);
                if (k1.basisComputationRequired) safeCheck(seForm, `topmostSubform[0].Page2[0].Table_Line28a-f[0].${rowName}[0].c2_${checkBaseId + 1}[0]`);
                if (k1.notAtRisk) safeCheck(seForm, `topmostSubform[0].Page2[0].Table_Line28a-f[0].${rowName}[0].c2_${checkBaseId + 2}[0]`);

                // --- RIGHT TABLE (Cols G - K): Table_Line28g-k ---
                // Row A starts at f2_15. Row B starts at f2_20. (Step = 5)
                const rightBaseId = 15 + (index * 5);

                // (g) Passive Loss Allowed
                const pLoss = parseFloat(k1.passiveLossAllowed) || 0;
                if (pLoss > 0) {
                    safeFill(seForm, `topmostSubform[0].Page2[0].Table_Line28g-k[0].${rowName}[0].f2_${rightBaseId}[0]`, formatMoney(pLoss));
                    totalPassiveLoss += pLoss;
                }

                // (h) Passive Income
                const pIncome = parseFloat(k1.passiveIncome) || 0;
                if (pIncome > 0) {
                    safeFill(seForm, `topmostSubform[0].Page2[0].Table_Line28g-k[0].${rowName}[0].f2_${rightBaseId + 1}[0]`, formatMoney(pIncome));
                    totalPassiveIncome += pIncome;
                }

                // (i) Nonpassive Loss
                const npLoss = parseFloat(k1.nonPassiveLoss) || 0;
                if (npLoss > 0) {
                    safeFill(seForm, `topmostSubform[0].Page2[0].Table_Line28g-k[0].${rowName}[0].f2_${rightBaseId + 2}[0]`, formatMoney(npLoss));
                    totalNonPassiveLoss += npLoss;
                }

                // (j) Section 179 Deduction
                const sec179 = parseFloat(k1.section179Deduction) || 0;
                if (sec179 > 0) {
                    safeFill(seForm, `topmostSubform[0].Page2[0].Table_Line28g-k[0].${rowName}[0].f2_${rightBaseId + 3}[0]`, formatMoney(sec179));
                    totalSection179 += sec179;
                }

                // (k) Nonpassive Income
                const npIncome = parseFloat(k1.nonPassiveIncome) || 0;
                if (npIncome > 0) {
                    safeFill(seForm, `topmostSubform[0].Page2[0].Table_Line28g-k[0].${rowName}[0].f2_${rightBaseId + 4}[0]`, formatMoney(npIncome));
                    totalNonPassiveIncome += npIncome;
                }
            });

            // --- LINE 29a: Totals for Income Columns (h) and (k) ---
            // IDs start at 35 (since Row D ended at 34)
            safeFill(seForm, 'topmostSubform[0].Page2[0].f2_36[0]', formatMoney(totalPassiveIncome)); // Col h (Income)
            safeFill(seForm, 'topmostSubform[0].Page2[0].f2_39[0]', formatMoney(totalNonPassiveIncome)); // Col k (Income)

            // --- LINE 29b: Totals for Loss Columns (g), (i), and (j) ---
            // IDs start at 40
            safeFill(seForm, 'topmostSubform[0].Page2[0].f2_40[0]', formatMoney(totalPassiveLoss)); // Col g (Loss)
            safeFill(seForm, 'topmostSubform[0].Page2[0].f2_42[0]', formatMoney(totalNonPassiveLoss)); // Col i (Loss)
            safeFill(seForm, 'topmostSubform[0].Page2[0].f2_43[0]', formatMoney(totalSection179)); // Col j (Ded)

            // --- LINE 30: Total Partnership/S-Corp Income ---
            // Sum of columns (h) and (k)
            const line30 = totalPassiveIncome + totalNonPassiveIncome;
            safeFill(seForm, 'topmostSubform[0].Page2[0].f2_45[0]', formatMoney(line30));

            // --- LINE 31: Total Partnership/S-Corp Loss ---
            // Sum of columns (g), (i), and (j)
            const line31 = totalPassiveLoss + totalNonPassiveLoss + totalSection179;
            safeFill(seForm, 'topmostSubform[0].Page2[0].f2_46[0]', formatMoney(line31));

            // --- LINE 32: Net Income or (Loss) ---
            const line32 = line30 - line31;
            safeFill(seForm, 'topmostSubform[0].Page2[0].f2_47[0]', formatMoney(line32));

            // ===========================================================================
            // PART III: INCOME OR LOSS FROM ESTATES AND TRUSTS
            // ===========================================================================

            const estates = schedE.estates || [];
            const estateRows = ['RowA', 'RowB'];
            
            // Base ID for Line 33, Row A, Column (a) is 64
            // (Because Part II Line 32 ended at f2_63)
            const estateBaseId = 48; 

            // Initialize Totals
            let totalEstPassiveLoss = 0;      // Col (c)
            let totalEstPassiveIncome = 0;    // Col (d)
            let totalEstNonPassiveLoss = 0;   // Col (e)
            let totalEstNonPassiveIncome = 0; // Col (f)

            // Loop through up to 2 Estates/Trusts
            estates.slice(0, 2).forEach((est, index) => {
                const rowName = estateRows[index];
                
                // Each row has 6 fields: Name, EIN, c, d, e, f
                // Step = 6
                const baseId = estateBaseId + (index * 6);
                
                let prefix1;
                let prefix2;

                if (index === 1) {
                    prefix1 = 'a-b[0].RowB'
                    prefix2 = 'c-f[0].RowB'
                } else {
                    prefix1 = 'a-b[0].RowA'
                    prefix2 = 'c-f[0].RowA'
                }

                // (a) Name
                safeFill(seForm, `topmostSubform[0].Page2[0].Table_Line33${prefix1}[0].f2_${baseId}[0]`, est.name);
                               
                // (b) EIN
                safeFill(seForm, `topmostSubform[0].Page2[0].Table_Line33${prefix1}[0].f2_${baseId + 1}[0]`, est.ein);

                // (c) Passive Deduction or Loss Allowed
                const pLoss = parseFloat(est.passiveLossAllowed) || 0;
                if (pLoss > 0) {
                    safeFill(seForm, `topmostSubform[0].Page2[0].Table_Line33${prefix2}[0].f2_${baseId + 2}[0]`, formatMoney(pLoss));
                    totalEstPassiveLoss += pLoss;
                }

                // (d) Passive Income
                const pIncome = parseFloat(est.passiveIncome) || 0;
                if (pIncome > 0) {
                    safeFill(seForm, `topmostSubform[0].Page2[0].Table_Line33${prefix2}[0].f2_${baseId + 3}[0]`, formatMoney(pIncome));
                    totalEstPassiveIncome += pIncome;
                }

                // (e) Deduction or Loss (Nonpassive)
                const npLoss = parseFloat(est.nonPassiveLoss) || 0;
                if (npLoss > 0) {
                    safeFill(seForm, `topmostSubform[0].Page2[0].Table_Line33${prefix2}[0].f2_${baseId + 4}[0]`, formatMoney(npLoss));
                    totalEstNonPassiveLoss += npLoss;
                }

                // (f) Other Income (Nonpassive)
                const npIncome = parseFloat(est.nonPassiveIncome) || 0;
                if (npIncome > 0) {
                    safeFill(seForm, `topmostSubform[0].Page2[0].Table_Line33${prefix2}[0].f2_${baseId + 5}[0]`, formatMoney(npIncome));
                    totalEstNonPassiveIncome += npIncome;
                }
            });

            // --- LINE 34a: Totals (Columns c, d, e, f) ---
            // Row B ends at 75 (64 + 11). Totals start at 76.
                   // Col c
            safeFill(seForm, 'topmostSubform[0].Page2[0].f2_61[0]', formatMoney(totalEstPassiveIncome));     // Col d
            
            safeFill(seForm, 'topmostSubform[0].Page2[0].f2_63[0]', formatMoney(totalEstNonPassiveIncome));  // Col f

            // --- LINE 34b: Totals from Attached Schedules ---
            safeFill(seForm, 'topmostSubform[0].Page2[0].f2_64[0]', formatMoney(totalEstPassiveLoss));

            safeFill(seForm, 'topmostSubform[0].Page2[0].f2_66[0]', formatMoney(totalEstNonPassiveLoss));    // Col e

            // --- LINE 35: Total Estate/Trust Income ---
            // Sum of columns (d) and (f) from 34a and 34b
            const line35 = totalEstPassiveIncome + totalEstNonPassiveIncome;
            safeFill(seForm, 'topmostSubform[0].Page2[0].f2_68[0]', formatMoney(line35));

            // --- LINE 36: Total Estate/Trust Loss ---
            // Sum of columns (c) and (e) from 34a and 34b
            const line36 = totalEstPassiveLoss + totalEstNonPassiveLoss;
            safeFill(seForm, 'topmostSubform[0].Page2[0].f2_69[0]', formatMoney(line36));

            // --- LINE 37: Total Estate/Trust Income or (Loss) ---
            // Line 35 - Line 36
            const line37 = line35 - line36;
            safeFill(seForm, 'topmostSubform[0].Page2[0].f2_70[0]', formatMoney(line37));

            // ===========================================================================
            // PART IV: INCOME OR LOSS FROM REAL ESTATE MORTGAGE INVESTMENT CONDUITS (REMICs)
            // ===========================================================================

            const remics = schedE.remics || [];
            
            // Base ID for Line 38, Column (a) is 87
            // (Because Part III Line 37 ended at f2_86)
            const remicBaseId = 71;

            let totalRemicIncome = 0; // Sum of Cols (d) and (e)

            // Schedule E only has 1 row for REMICs on the main form (Line 38)
            remics.slice(0, 1).forEach((remic, index) => {
                // (a) Name
                safeFill(seForm, `topmostSubform[0].Page2[0].Table_Line38[0].Row1[0].f2_${remicBaseId}[0]`, remic.name);
                
                // (b) EIN
                safeFill(seForm, `topmostSubform[0].Page2[0].Table_Line38[0].Row1[0].f2_${remicBaseId + 1}[0]`, remic.ein);

                // (c) Excess Inclusion (Schedules Q, line 2c)
                if (parseFloat(remic.excessInclusion) > 0) {
                    safeFill(seForm, `topmostSubform[0].Page2[0].Table_Line38[0].Row1[0].f2_${remicBaseId + 2}[0]`, formatMoney(remic.excessInclusion));
                }

                // (d) Taxable Income/Net Loss (Schedules Q, line 1b)
                const taxIncome = parseFloat(remic.taxableIncome) || 0;
                if (taxIncome !== 0) {
                    safeFill(seForm, `topmostSubform[0].Page2[0].Table_Line38[0].Row1[0].f2_${remicBaseId + 3}[0]`, formatMoney(taxIncome));
                    totalRemicIncome += taxIncome;
                }

                // (e) Income (Schedules Q, line 3b)
                const schedQIncome = parseFloat(remic.incomeScheduleQ) || 0;
                if (schedQIncome > 0) {
                    safeFill(seForm, `topmostSubform[0].Page2[0].Table_Line38[0].Row1[0].f2_${remicBaseId + 4}[0]`, formatMoney(schedQIncome));
                    totalRemicIncome += schedQIncome;
                }
            });

            // --- LINE 39: Total REMIC Income ---
            // Combine columns (d) and (e)
            safeFill(seForm, 'topmostSubform[0].Page2[0].f2_76[0]', formatMoney(totalRemicIncome));


            // ===========================================================================
            // PART V: SUMMARY
            // ===========================================================================

            // --- LINE 40: Net Farm Rental Income (Form 4835) ---
            // Assuming you might add this to formData later, defaulting to 0 for now
            const farmRentalIncome = parseFloat(formData.farmRentalIncome) || 0;
            safeFill(seForm, 'topmostSubform[0].Page2[0].f2_77[0]', formatMoney(farmRentalIncome));

            // --- LINE 41: Total Income or (Loss) ---
            // Combine Lines 26, 32, 37, 39, and 40
            const line26 = sumLine24 - sumLine25; // Rental Real Estate
            const line32e = (totalPassiveIncome + totalNonPassiveIncome) - (totalPassiveLoss + totalNonPassiveLoss + totalSection179); // Partnerships
            const line37e = (totalEstPassiveIncome + totalEstNonPassiveIncome) - (totalEstPassiveLoss + totalEstNonPassiveLoss); // Estates
            const line39 = totalRemicIncome; // REMICs
            const line40 = farmRentalIncome; // Farm

            const totalScheduleEIncome = line26 + line32e + line37e + line39 + line40;
            
            safeFill(seForm, 'topmostSubform[0].Page2[0].f2_78[0]', formatMoney(totalScheduleEIncome));

            // --- LINE 42: Reconciliation for Farming/Fishing (Optional) ---
            // safeFill(seForm, 'topmostSubform[0].Page2[0].f2_95[0]', ...);

            // --- LINE 43: Reconciliation for Real Estate Professionals (Optional) ---
            // safeFill(seForm, 'topmostSubform[0].Page2[0].f2_96[0]', ...);

            seForm.flatten();
            const sePages = await pdfDoc.copyPages(seDoc, seDoc.getPageIndices());
            sePages.forEach(page => pdfDoc.addPage(page));

        } catch (e) {
            console.error("Error generating Schedule E:", e);
        }
    }

    if (formData.nonresidentAlienStatement && formData.nonresidentAlienStatement instanceof File) {
        try {
            console.log("Attaching Nonresident Alien Statement...");
            const file = formData.nonresidentAlienStatement;
            const fileBytes = await file.arrayBuffer();

            if (file.type === 'application/pdf') {
                // --- MERGE PDF ---
                const attachmentDoc = await PDFDocument.load(fileBytes);
                const attachmentPages = await pdfDoc.copyPages(attachmentDoc, attachmentDoc.getPageIndices());
                attachmentPages.forEach(page => pdfDoc.addPage(page));
            } 
            else if (file.type === 'image/jpeg' || file.type === 'image/png') {
                // --- EMBED IMAGE ---
                let image;
                if (file.type === 'image/jpeg') {
                    image = await pdfDoc.embedJpg(fileBytes);
                } else {
                    image = await pdfDoc.embedPng(fileBytes);
                }

                // Create a new page matching the document size (usually Letter)
                const page = pdfDoc.addPage();
                const { width, height } = page.getSize();
                
                // Scale image to fit within margins
                const margin = 50;
                const maxWidth = width - (margin * 2);
                const maxHeight = height - (margin * 2);
                const imgDims = image.scaleToFit(maxWidth, maxHeight);

                page.drawImage(image, {
                    x: margin,
                    y: height - margin - imgDims.height, // Draw from top-left
                    width: imgDims.width,
                    height: imgDims.height,
                });
            } 
            else {
                console.warn("Unsupported attachment type:", file.type);
                alert(`Skipped attachment: ${file.name} (Unsupported file type). Please convert to PDF, JPG, or PNG.`);
            }
        } catch (e) {
            console.error("Error attaching file:", e);
            alert("Could not attach the Nonresident Alien Statement. Please print and attach it manually.");
        }
    }

    if (!isPaid) {
    const pages = pdfDoc.getPages();
    const font = await pdfDoc.embedFont('Helvetica-Bold'); 
    pages.forEach(page => drawWatermark(page, font));
  }


    // --- SAVE FINAL PDF ---
    const pdfBytes = await pdfDoc.save();
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    saveAs(blob, `1040_Return_${new Date().getFullYear()}.pdf`);

  } catch (error) {
    console.error("Error generating PDF:", error);
    alert("Failed to generate PDF. Check console for details.");
  }
};

// --- HELPERS ---

const safeFill = (form, fieldName, value) => {
  try {
    const field = form.getTextField(fieldName);
    if (field) {
      field.setText(String(value));
      // Add this log to see what's happening
      console.log(`SUCCESS: Filled ${fieldName} with "${value}"`);
    }
  } catch (e) {
    // Add this log to see failures
    console.warn(`FAILURE: Could not find field ${fieldName}`);
  }
};

const safeCheck = (form, fieldName) => {
  try {
    const field = form.getCheckBox(fieldName);
    if (field) field.check();
  } catch (e) {
    // console.warn(`Checkbox not found: ${fieldName}`);
  }
};

const formatMoney = (num) => {
  const value = parseFloat(num) || 0; 
  return value.toFixed(2);
};


/*
  ===========================================================================
  DEVELOPER NOTE: ROADMAP TO "COMPLETE" FILING STATUS
  ===========================================================================
  
  Current Status: "Calculator / Drafter"
  - Generates a mathematically correct Form 1040 for simple cases.
  - Calculates Refund/Owe correctly.
  
  MISSING COMPONENTS FOR >95% COVERAGE (Self-Employed & Investors):
  
  1. SCHEDULE C (Profit or Loss from Business) [CRITICAL]
     - Current: We calculate 'netBusinessIncome'.
     - Required: Generate the actual Schedule C PDF showing expenses breakdown.
     - Source: 'expensesData' array.
  
  2. SCHEDULE SE (Self-Employment Tax) [CRITICAL]
     - Current: We calculate 'selfEmploymentTax' (15.3%).
     - Required: Attach Schedule SE to prove the math.
     - Source: 'netBusinessIncome' from Schedule C.
  
  3. FORM 8995 (QBI Deduction) [HIGH PRIORITY]
     - Current: We calculate 'line13' (20% deduction).
     - Required: Attach Form 8995 to claim this "free money" deduction.
  
  4. FORM 8949 (Sales of Capital Assets) [CRITICAL]
     - Current: We calculate Net Gain/Loss.
     - Required: List EVERY single trade (Date Sold, Proceeds, Cost) on Form 8949.
     - Source: 'investmentsData' array.
  
  5. STATE TAXES
     - Current: Simple estimate for CA.
     - Required: Separate logic/forms for all 41 income-taxing states.
  
  ARCHITECTURE CHANGE (Implemented Below):
  - All logic (Refund vs Owe, Penalties) is now in 'totals' useMemo.
  - The PDF Generator is now "dumb" and just prints what 'totals' tells it.
  ===========================================================================
*/
